import L from "leaflet";
import * as turf from "@turf/turf";

const STATE_KEY = "hydrotools:mbswath-planner:v1";
const MAX_LINES = 500;
const MIN_SPACING_M = 5;
const MIN_LINE_LENGTH_M = 10;

function esc(v) {
  return String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function defaultState() {
  return {
    azimuth: 0,
    swathAngle: 120,
    overlap: 10,
    minDepth: 5,
    mode: "azimuth",
    boundaryCoords: null,
    spineCoords: null,
    sectionCoords: null,
    sectionSampleSpacing: 25,
  };
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return defaultState();
    const s = { ...defaultState(), ...parsed };
    s.azimuth = Math.max(0, Math.min(359, Number(s.azimuth) || 0));
    s.swathAngle = Math.max(1, Math.min(179, Number(s.swathAngle) || 120));
    s.overlap = Math.max(0, Math.min(95, Number(s.overlap) || 10));
    s.minDepth = Math.max(0.5, Number(s.minDepth) || 5);
    s.mode = ["azimuth", "spine", "section"].includes(s.mode) ? s.mode : "azimuth";
    s.boundaryCoords = Array.isArray(s.boundaryCoords) && s.boundaryCoords.length >= 3 ? s.boundaryCoords : null;
    s.spineCoords = Array.isArray(s.spineCoords) && s.spineCoords.length >= 2 ? s.spineCoords : null;
    s.sectionCoords = Array.isArray(s.sectionCoords) && s.sectionCoords.length >= 2 ? s.sectionCoords : null;
    s.sectionSampleSpacing = Math.max(1, Math.min(5000, Number(s.sectionSampleSpacing) || 25));
    return s;
  } catch {
    return defaultState();
  }
}

function persistState(s) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify({
      azimuth: s.azimuth, swathAngle: s.swathAngle, overlap: s.overlap,
      minDepth: s.minDepth, mode: s.mode,
      boundaryCoords: s.boundaryCoords, spineCoords: s.spineCoords,
      sectionCoords: s.sectionCoords, sectionSampleSpacing: s.sectionSampleSpacing,
    }));
  } catch {}
}

function projectOntoAxis(pt, origin, bearingDeg) {
  const dist = turf.distance(origin, pt, { units: "meters" });
  if (dist < 0.0001) return 0;
  const bear = turf.bearing(origin, pt);
  return dist * Math.cos(((bear - bearingDeg) * Math.PI) / 180);
}

function clipLineToPoly(lineCoords, polygon) {
  const line = turf.lineString(lineCoords);
  let polyOutline;
  try { polyOutline = turf.polygonToLine(polygon); } catch { return null; }
  let result;
  try { result = turf.lineIntersect(line, polyOutline); } catch { return null; }
  const pts = result?.features ?? [];
  if (pts.length < 2) {
    const mid = [(lineCoords[0][0] + lineCoords[1][0]) / 2, (lineCoords[0][1] + lineCoords[1][1]) / 2];
    try { if (turf.booleanPointInPolygon(turf.point(mid), polygon)) return lineCoords; } catch {}
    return null;
  }
  const startPt = turf.point(lineCoords[0]);
  pts.sort((a, b) => turf.distance(startPt, a, { units: "meters" }) - turf.distance(startPt, b, { units: "meters" }));
  return [pts[0].geometry.coordinates, pts[pts.length - 1].geometry.coordinates];
}

// Extend both ends of a polyline outward along its tangent by distM meters.
function extendPolylineEnds(coords, distM) {
  if (coords.length < 2) return coords;
  const km = distM / 1000;
  const startBear = turf.bearing(turf.point(coords[1]), turf.point(coords[0]));
  const newStart = turf.destination(turf.point(coords[0]), km, startBear, { units: "kilometers" }).geometry.coordinates;
  const n = coords.length;
  const endBear = turf.bearing(turf.point(coords[n - 2]), turf.point(coords[n - 1]));
  const newEnd = turf.destination(turf.point(coords[n - 1]), km, endBear, { units: "kilometers" }).geometry.coordinates;
  return [newStart, ...coords, newEnd];
}

// Snap a point just inside to the exact polygon edge using the adjacent outside point.
function snapToPolygonEdge(insidePt, outsidePt, polygon) {
  try {
    const polyOutline = turf.polygonToLine(polygon);
    const ints = turf.lineIntersect(turf.lineString([outsidePt, insidePt]), polyOutline);
    if (ints.features.length > 0) return ints.features[0].geometry.coordinates;
  } catch {}
  return insidePt;
}

// Clip a multi-segment polyline to a polygon: densify to ~40m steps, find the
// largest contiguous inside-polygon run, snap endpoints to exact boundary crossing.
function clipPolylineToBoundary(coords, polygon) {
  if (!coords || coords.length < 2) return null;
  const dense = [];
  for (let i = 0; i < coords.length - 1; i++) {
    const [x0, y0] = coords[i], [x1, y1] = coords[i + 1];
    const dx = x1 - x0, dy = y1 - y0;
    const steps = Math.max(2, Math.ceil(Math.sqrt(dx * dx + dy * dy) / 0.00036));
    for (let s = 0; s < steps; s++) dense.push([x0 + dx * (s / steps), y0 + dy * (s / steps)]);
  }
  dense.push(coords[coords.length - 1]);
  const inside = dense.map((c) => { try { return turf.booleanPointInPolygon(turf.point(c), polygon); } catch { return false; } });
  let bestStart = -1, bestLen = 0, curStart = -1, curLen = 0;
  for (let i = 0; i < inside.length; i++) {
    if (inside[i]) {
      if (curStart === -1) curStart = i;
      curLen++;
      if (curLen > bestLen) { bestLen = curLen; bestStart = curStart; }
    } else { curStart = -1; curLen = 0; }
  }
  if (bestLen < 2) return null;
  const result = dense.slice(bestStart, bestStart + bestLen);
  // Snap endpoints to exact polygon boundary crossing
  if (bestStart > 0) result[0] = snapToPolygonEdge(dense[bestStart], dense[bestStart - 1], polygon);
  const endIdx = bestStart + bestLen - 1;
  if (endIdx + 1 < dense.length) result[result.length - 1] = snapToPolygonEdge(dense[endIdx], dense[endIdx + 1], polygon);
  return result;
}

// Like clipPolylineToBoundary but returns ALL inside-polygon runs, not just the largest.
// This catches water areas separated by land (e.g. inner harbour behind a peninsula).
function clipPolylineToPolygonAll(coords, polygon) {
  if (!coords || coords.length < 2) return [];
  const dense = [];
  for (let i = 0; i < coords.length - 1; i++) {
    const [x0, y0] = coords[i], [x1, y1] = coords[i + 1];
    const dx = x1 - x0, dy = y1 - y0;
    const steps = Math.max(2, Math.ceil(Math.sqrt(dx * dx + dy * dy) / 0.00036));
    for (let s = 0; s < steps; s++) dense.push([x0 + dx * (s / steps), y0 + dy * (s / steps)]);
  }
  dense.push(coords[coords.length - 1]);
  const inside = dense.map((c) => { try { return turf.booleanPointInPolygon(turf.point(c), polygon); } catch { return false; } });
  const runs = [];
  let curStart = -1;
  for (let i = 0; i <= inside.length; i++) {
    if (i < inside.length && inside[i]) { if (curStart === -1) curStart = i; }
    else if (curStart !== -1) {
      if (i - curStart >= 2) {
        const run = dense.slice(curStart, i);
        if (curStart > 0) run[0] = snapToPolygonEdge(dense[curStart], dense[curStart - 1], polygon);
        if (i < dense.length) run[run.length - 1] = snapToPolygonEdge(dense[i - 1], dense[i], polygon);
        runs.push(run);
      }
      curStart = -1;
    }
  }
  return runs;
}

function azimuthLabel(deg) {
  const d = ((deg % 360) + 360) % 360;
  const labels = ["N","NNE","NNE","NE","NE","ENE","ENE","E","E","ESE","ESE","SE","SE","SSE","SSE","S","S","SSW","SSW","SW","SW","WSW","WSW","W","W","WNW","WNW","NW","NW","NNW","NNW","N"];
  return labels[Math.round(d / 11.25) % 32];
}

function previewCalc(swathAngle, minDepth, overlap) {
  const swath = 2 * minDepth * Math.tan((swathAngle / 2) * Math.PI / 180);
  const spacing = swath * (1 - overlap / 100);
  return { swath: Math.round(swath), spacing: Math.round(spacing) };
}

function buildSpineSegments(spineCoords) {
  const segments = [];
  let cumLen = 0;
  for (let i = 0; i < spineCoords.length - 1; i++) {
    const p1 = turf.point(spineCoords[i]);
    const p2 = turf.point(spineCoords[i + 1]);
    const len = turf.distance(p1, p2, { units: "meters" });
    const bearing = turf.bearing(p1, p2);
    segments.push({ start: spineCoords[i], end: spineCoords[i + 1], bearing, length: len, cumStart: cumLen });
    cumLen += len;
  }
  return { segments, totalLength: cumLen };
}

function getSpinePositionAt(segments, totalLength, s) {
  const clamped = Math.max(0, Math.min(totalLength, s));
  for (const seg of segments) {
    if (clamped <= seg.cumStart + seg.length + 1e-6) {
      const t = seg.length > 0 ? (clamped - seg.cumStart) / seg.length : 0;
      const pt = turf.destination(turf.point(seg.start), t * seg.length, seg.bearing, { units: "meters" });
      return { point: pt.geometry.coordinates, bearing: seg.bearing };
    }
  }
  const last = segments[segments.length - 1];
  return { point: last.end, bearing: last.bearing };
}

function ringSignedArea(ring) {
  let s = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    s += (x1 * y2) - (x2 * y1);
  }
  return s / 2;
}

// ENC depth-area (DEPARE) geometry is always Polygon/MultiPolygon, so this only
// needs the rings branch of the general esri-geometry conversion.
function esriPolygonToGeoJSON(geom) {
  if (!geom || !Array.isArray(geom.rings) || !geom.rings.length) return null;
  const outers = [];
  const holes = [];
  for (const ring of geom.rings) {
    if (!Array.isArray(ring) || ring.length < 4) continue;
    if (ringSignedArea(ring) < 0) outers.push([ring]);
    else holes.push(ring);
  }
  if (!outers.length) return { type: "Polygon", coordinates: [geom.rings[0], ...geom.rings.slice(1)] };
  for (const h of holes) {
    const owner = outers.find((o) => { try { return turf.booleanPointInPolygon(turf.point(h[0]), turf.polygon(o)); } catch { return false; } });
    if (owner) owner.push(h);
  }
  return outers.length === 1 ? { type: "Polygon", coordinates: outers[0] } : { type: "MultiPolygon", coordinates: outers };
}

function createRuntime(app) {
  const map = app.map.instance;
  const mapEl = app.map.el;

  const boundaryLayer = L.layerGroup().addTo(map);
  const spineLayer = L.layerGroup().addTo(map);
  const sectionLayer = L.layerGroup().addTo(map);
  const linesLayer = L.layerGroup().addTo(map);
  const drawLayer = L.layerGroup().addTo(map);
  const contourLayer = L.layerGroup().addTo(map);

  let state = readState();
  let containerEl = null;

  let drawingBoundary = false, drawVertices = [], drawHandlers = [];
  let drawingAzimuth = false, azimuthPt1 = null, azimuthDrawHandlers = [];
  let drawingSpine = false, spineVertices = [], spineDrawHandlers = [];
  let drawingSection = false, sectionVertices = [], sectionDrawHandlers = [];

  let generating = false, generateAbort = false;
  let contourDrawn = false;
  let waterGrid = null, waterGridBbox = null, waterGridN = 0;
  let lines = [], stats = null, status = "";

  // ── Statistical LNM Estimator ─────────────────────────────────────────────
  const ETOPO_SAMPLES_URL = "https://gis.ngdc.noaa.gov/arcgis/rest/services/DEM_mosaics/DEM_global_mosaic/ImageServer/getSamples";
  const GEBCO_SAMPLES_URL = "https://tiles.arcgis.com/tiles/C8EMgrsFcRFL6LrL/arcgis/rest/services/GEBCO_elevation_wgs84/ImageServer/getSamples";
  const ENC_DEPARE_SERVICE_URL = "https://encdirect.noaa.gov/arcgis/rest/services/encdirect/enc_harbour/MapServer/227";
  const STAT_ENC_MAX_DEPTH_M = 20; // ENC depth-area polygons handle <20m; raster grid takes over at/above this
  const statLayer = L.layerGroup().addTo(map);
  const statContourLayer = L.layerGroup().addTo(map);
  const statLabelLayer = L.layerGroup().addTo(map);

  let statAOIs = [];             // [{ id, name, source, poly, areaM2, bins, computing, collapsed, status, outlineLayer }]
  let statComputing = false;
  let statDrawing = false;
  let _statDrawHandlers = null;
  let statShowContours = false;
  let statShowLabels = false;
  let statDamFiles = [], statDamLoading = false, statDamOpen = false;

  let statCovXwd = 4, statCrosslinePct = 4, statShoalPct = 0, statRunInM = 200, statDepthSrc = "etopo";
  let statUseEnc = true;
  const COST_RATES_KEY = "hydrotools:mbswath-planner:cost-rates:v1";
  let statCostRates = (() => { try { return JSON.parse(localStorage.getItem(COST_RATES_KEY)) || {}; } catch { return {}; } })();
  function saveCostRates() { try { localStorage.setItem(COST_RATES_KEY, JSON.stringify(statCostRates)); } catch {} }

  const STAT_RANGES = [
    { label: "5\u201310 m",    minM: 5,   maxM: 10,  color: "#a5f3fc", binSizeM: 125  },
    { label: "10\u201320 m",   minM: 10,  maxM: 20,  color: "#22d3ee", binSizeM: 250  },
    { label: "20\u201340 m",   minM: 20,  maxM: 40,  color: "#0891b2", binSizeM: 500  },
    { label: "40\u201380 m",   minM: 40,  maxM: 80,  color: "#1d4ed8", binSizeM: 1000 },
    { label: "80\u2013160 m",  minM: 80,  maxM: 160, color: "#3730a3", binSizeM: 2000 },
    { label: "160\u2013320 m", minM: 160, maxM: 320, color: "#4c1d95", binSizeM: 2000 },
    { label: "320\u2013640 m", minM: 320, maxM: 640, color: "#1e1b4b", binSizeM: 2000 },
    { label: "640 m+",          minM: 640, maxM: Infinity, color: "#0a0920", binSizeM: 2000 },
  ];

  // Each level refines bins from the previous pass that are shallower than belowDepthM.
  // First level uses Infinity so ALL 4000 m bins refine to 2000 m \u2014 eliminates large dark
  // coastline artifacts where ETopo's coarse grid assigns deep values to nearshore centroids.
  const STAT_REFINE_LEVELS = [
    { fromSizeM: 4000, toSizeM: 2000, belowDepthM: Infinity },
    { fromSizeM: 2000, toSizeM: 1000, belowDepthM: 80  },
    { fromSizeM: 1000, toSizeM: 500,  belowDepthM: 40  },
    { fromSizeM: 500,  toSizeM: 250,  belowDepthM: 20  },
    { fromSizeM: 250,  toSizeM: 125,  belowDepthM: 10  },
  ];

  const STAT_CONTOUR_DEPTHS = [5, 10, 20, 40, 80, 160, 320];

  function statGetRange(depthM) {
    return STAT_RANGES.find(r => depthM >= r.minM && depthM < r.maxM) ?? STAT_RANGES.at(-1);
  }

  function computeStatRow(areaAcres, depthM, cov, xlPct, shoalPct, runInM) {
    const areaM2 = areaAcres * 4046.85642;
    const swathM = cov * depthM;
    if (swathM <= 0) return null;
    const sqrtA = Math.sqrt(areaM2);
    const nLines = sqrtA / swathM;   // continuous fraction — area ÷ swath; no integer floor so each bin's unique depth drives a unique contribution
    const lineLenM = sqrtA + runInM;
    const mainM = nLines * lineLenM;
    const xlM = mainM * (xlPct / 100);
    const shoalM = (mainM + xlM) * (shoalPct / 100);
    const totalM = mainM + xlM + shoalM;
    const NM = 0.000539957;
    return { swathM, nLines, mainNm: mainM * NM, xlNm: xlM * NM, shoalNm: shoalM * NM, totalNm: totalM * NM };
  }

  function groupAOIBins(aoi) {
    const out = STAT_RANGES.map(r => ({ ...r, bins: [], totalAcres: 0, totalMainNm: 0, totalXlNm: 0, totalNm: 0 }));
    for (const bin of aoi.bins) {
      if (bin.depthM == null || !bin.result) continue;
      const r = out.find(r => bin.depthM >= r.minM && bin.depthM < r.maxM);
      if (!r) continue;
      r.bins.push(bin);
      r.totalAcres += bin.areaAcres;
      r.totalMainNm += bin.result.mainNm;
      r.totalXlNm += bin.result.xlNm;
      r.totalNm += bin.result.totalNm;
    }
    return out.filter(r => r.bins.length > 0);
  }

  function makeStatAOI(name, poly, source) {
    return { id: `sa-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name, source: source || "draw", poly, areaM2: turf.area(poly), bins: [], computing: false, collapsed: false, status: "", outlineLayer: null };
  }
  function addStatAOI(name, poly, source) {
    const aoi = makeStatAOI(name, poly, source);
    const outline = aoi.poly.geometry.coordinates[0].map(([lng, lat]) => [lat, lng]);
    aoi.outlineLayer = L.polygon(outline, { color: "#f59e0b", weight: 2, opacity: 0.9, fillOpacity: 0, dashArray: "6 4", interactive: false }).addTo(statLayer);
    statAOIs.push(aoi);
    render();
    return aoi;
  }
  function removeStatAOI(id) {
    const idx = statAOIs.findIndex(a => a.id === id);
    if (idx < 0) return;
    const aoi = statAOIs[idx];
    aoi.bins.forEach(b => b.layer?.remove());
    aoi.outlineLayer?.remove();
    statAOIs.splice(idx, 1);
    if (statShowContours) generateStatContours().catch(console.warn);
    updateStatLabels();
    render();
  }

  function createStatBins(clipPoly, sizeM) {
    const cellKm = sizeM / 1000;
    const grid = turf.squareGrid(turf.bbox(clipPoly), cellKm, { units: "kilometers" });
    const bins = [];
    for (const cell of grid.features) {
      let clipped;
      try { clipped = turf.intersect(turf.featureCollection([cell, clipPoly])); } catch { clipped = null; }
      if (!clipped) continue;
      const areaM2 = turf.area(clipped);
      if (areaM2 < 100) continue;
      const centroid = turf.centroid(clipped).geometry.coordinates;
      bins.push({ poly: clipped, areaM2, areaAcres: areaM2 / 4046.85642, centroid, sizeM, depthM: null, result: null, layer: null, maxDepthM: null, parentDepth: null, isLand: false });
    }
    return bins;
  }

  function addStatBinsToMap(bins) {
    for (const bin of bins) {
      const coords = bin.poly.geometry.coordinates[0].map(([lng, lat]) => [lat, lng]);
      bin.layer = L.polygon(coords, {
        color: "#334155", weight: 0.5, opacity: 0.5,
        fillColor: "#1e293b", fillOpacity: 0.6, interactive: false,
      }).addTo(statLayer);
    }
  }

  function colorStatBin(bin) {
    if (!bin.layer || bin.depthM == null) return;
    const r = statGetRange(bin.depthM);
    bin.layer.setStyle({ fillColor: r.color, color: r.color, fillOpacity: 0.45, opacity: 0.65, weight: 0.5 });
  }

  // Fetch ENC depth-area (DEPARE) polygons overlapping the AOI bbox, keeping only
  // bands that touch the <20m range we want ENC to cover instead of the raster grid.
  async function fetchEncDepareForAOI(aoiPoly) {
    const [xmin, ymin, xmax, ymax] = turf.bbox(aoiPoly);
    const geometry = JSON.stringify({ xmin, ymin, xmax, ymax, spatialReference: { wkid: 4326 } });
    const features = [];
    let resultOffset = 0;
    let keepPaging = true;
    try {
      while (keepPaging) {
        const query = new URLSearchParams({
          where: "1=1", outFields: "DRVAL1,DRVAL2", f: "json",
          returnGeometry: "true", outSR: "4326", inSR: "4326",
          geometryType: "esriGeometryEnvelope", geometry,
          spatialRel: "esriSpatialRelIntersects",
          resultRecordCount: "1000", resultOffset: String(resultOffset),
        });
        const res = await fetch(`${ENC_DEPARE_SERVICE_URL}/query?${query}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const page = await res.json();
        if (page.error) throw new Error(page.error.message || "ENC service error");
        const pageFeatures = Array.isArray(page.features) ? page.features : [];
        for (const f of pageFeatures) {
          const geom = esriPolygonToGeoJSON(f.geometry);
          if (!geom) continue;
          const drval1 = Number(f.attributes?.DRVAL1);
          const drval2 = Number(f.attributes?.DRVAL2);
          if (!Number.isFinite(drval2) || drval2 <= 0 || !(drval1 < STAT_ENC_MAX_DEPTH_M)) continue;
          features.push(turf.feature(geom, { DRVAL1: Number.isFinite(drval1) ? drval1 : 0, DRVAL2: drval2 }));
        }
        resultOffset += pageFeatures.length;
        keepPaging = !!page.exceededTransferLimit && pageFeatures.length > 0;
      }
    } catch (e) {
      console.warn("[StatLNM] ENC fetch:", e);
      return [];
    }
    return features;
  }

  // Clip each ENC depth-area polygon to the AOI and turn the result directly into
  // stat bins (area + depth both known up front — no raster sampling needed).
  // Returns the union of everything claimed so the raster grid can skip it.
  function buildEncShallowBins(aoiPoly, depareFeatures) {
    const bins = [];
    for (const feature of depareFeatures) {
      let clipped;
      try { clipped = turf.intersect(turf.featureCollection([feature, aoiPoly])); } catch { clipped = null; }
      if (!clipped) continue;
      const depthM = (feature.properties.DRVAL1 + feature.properties.DRVAL2) / 2;
      let flat;
      try { flat = turf.flatten(clipped); } catch { flat = null; }
      if (!flat) continue;
      for (const part of flat.features) {
        const areaM2 = turf.area(part);
        if (areaM2 < 100) continue;
        const areaAcres = areaM2 / 4046.85642;
        const centroid = turf.centroid(part).geometry.coordinates;
        bins.push({
          poly: part, areaM2, areaAcres, centroid, sizeM: null, depthM,
          result: computeStatRow(areaAcres, depthM, statCovXwd, statCrosslinePct, statShoalPct, statRunInM),
          layer: null, maxDepthM: null, parentDepth: null, isLand: false, source: "enc",
        });
      }
    }
    let coverage = null;
    if (bins.length === 1) coverage = bins[0].poly;
    else if (bins.length > 1) {
      try { coverage = turf.union(turf.featureCollection(bins.map((b) => b.poly))); } catch { coverage = null; }
    }
    return { bins, coverage };
  }

  const STAT_BATCH = 80;
  const STAT_CONCURRENT = 5;

  async function sampleStatBatch(bins) {
    const pts = bins.map(b => b.centroid);
    const url = statDepthSrc === "gebco" ? GEBCO_SAMPLES_URL : ETOPO_SAMPLES_URL;
    const res = await fetch(`${url}?${new URLSearchParams({
      geometry: JSON.stringify({ points: pts, spatialReference: { wkid: 4326 } }),
      geometryType: "esriGeometryMultipoint",
      sampleCount: String(pts.length),
      f: "json",
    })}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const j = await res.json();
    if (j.error) throw new Error(j.error.message || "service error");
    for (const s of j.samples || []) {
      const val = Number(Array.isArray(s.value) ? s.value[0] : s.value);
      const sx = s.location?.x ?? s.locationGeometry?.x;
      const sy = s.location?.y ?? s.locationGeometry?.y;
      if (sx == null || sy == null) continue;
      // Always find the nearest bin so we can tag land explicitly.
      let best = null, bestD = Infinity;
      for (const bin of bins) {
        const dx = bin.centroid[0] - sx, dy = bin.centroid[1] - sy;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = bin; }
      }
      if (!best) continue;
      if (!Number.isFinite(val) || val > 0) {
        // ETopo confirms land (positive elevation) — mark so parentDepth fallback skips it.
        if (val > 0) best.isLand = true;
        continue;
      }
      if (best.depthM == null) {
        const depthM = Math.abs(val);
        // Plausibility: child bins inherit a max depth from their parent's refinement threshold.
        // ETopo can return deep interpolated values at coastline centroids — reject those.
        if (best.maxDepthM != null && depthM > best.maxDepthM) continue;
        best.depthM = depthM;
        best.result = computeStatRow(best.areaAcres, depthM, statCovXwd, statCrosslinePct, statShoalPct, statRunInM);
        colorStatBin(best);
      }
    }
  }

  async function sampleStatWaves(bins, label, aoi) {
    for (let i = 0; i < bins.length; i += STAT_BATCH * STAT_CONCURRENT) {
      const wave = [];
      for (let j = i; j < Math.min(i + STAT_BATCH * STAT_CONCURRENT, bins.length); j += STAT_BATCH) {
        wave.push(sampleStatBatch(bins.slice(j, j + STAT_BATCH)).catch(e => console.warn("[StatLNM] batch:", e)));
      }
      await Promise.all(wave);
      const done = aoi.bins.filter(b => b.depthM != null).length;
      aoi.status = `${label} \u2014 ${done}\u202f/\u202f${aoi.bins.length} bins`;
      render();
    }
  }

  async function generateStatContours() {
    statContourLayer.clearLayers();
    if (!statShowContours || !statAOIs.length) return;
    const sampled = statAOIs.flatMap(a => a.bins).filter(b => b.depthM != null);
    if (sampled.length < 6) return;
    try {
      const allPolys = turf.featureCollection(statAOIs.map(a => a.poly));
      const bbox = turf.bbox(allPolys);
      const N = 60;
      const dLon = (bbox[2] - bbox[0]) / (N - 1);
      const dLat = (bbox[3] - bbox[1]) / (N - 1);
      const gridPts = [];
      for (let j = 0; j < N; j++) {
        for (let i = 0; i < N; i++) {
          const lon = bbox[0] + i * dLon;
          const lat = bbox[1] + j * dLat;
          const sorted = sampled
            .map(b => { const dx = b.centroid[0] - lon, dy = b.centroid[1] - lat; return { b, d2: dx * dx + dy * dy }; })
            .sort((a, b2) => a.d2 - b2.d2).slice(0, 4);
          // Skip grid points with no nearby sampled bin — prevents contours from
          // extrapolating over land when the AOI extends beyond the water area
          const MAX_IDW_D2 = 0.05 * 0.05;
          if (!sorted.length || sorted[0].d2 > MAX_IDW_D2) continue;
          let sw = 0, wd = 0;
          for (const { b, d2 } of sorted) { const w = 1 / Math.max(d2, 1e-12); sw += w; wd += w * b.depthM; }
          gridPts.push(turf.point([lon, lat], { depth: sw > 0 ? wd / sw : 0 }));
        }
      }
      const isoLines = turf.isolines(turf.featureCollection(gridPts), STAT_CONTOUR_DEPTHS, { zProperty: "depth" });
      for (const feature of isoLines.features) {
        const depth = Number(feature.properties.depth);
        const r = statGetRange(depth);
        const color = r?.color ?? "#94a3b8";
        const geom = feature.geometry;
        const segs = geom.type === "MultiLineString" ? geom.coordinates : [geom.coordinates];
        let longestSeg = null, longestLen = 0;
        for (const seg of segs) {
          L.polyline(seg.map(([ln, la]) => [la, ln]), { color, weight: 1.5, opacity: 0.9, interactive: false }).addTo(statContourLayer);
          if (seg.length > longestLen) { longestLen = seg.length; longestSeg = seg; }
        }
        if (longestSeg) {
          const mid = longestSeg[Math.floor(longestSeg.length / 2)];
          L.marker([mid[1], mid[0]], {
            icon: L.divIcon({ html: `<span style="font-size:9px;color:${color};font-weight:700;text-shadow:0 0 3px #000,0 0 3px #000;">${depth}m</span>`, className: "", iconSize: [30, 12], iconAnchor: [15, 6] }),
            interactive: false,
          }).addTo(statContourLayer);
        }
      }
    } catch (e) { console.warn("[StatLNM] contours:", e); }
  }

  function updateStatLabels() {
    statLabelLayer.clearLayers();
    if (!statShowLabels) return;
    for (const aoi of statAOIs) {
      for (const bin of aoi.bins) {
        if (bin.depthM == null || !bin.result) continue;
        const [lng, lat] = bin.centroid;
        // Two lines: ETopo depth and this bin's total LNM contribution
        const html = `<div style="font-size:8px;line-height:1.4;text-align:center;color:#fff;` +
          `text-shadow:0 0 2px #000,0 0 2px #000,0 0 2px #000;font-weight:700;white-space:nowrap;pointer-events:none;">` +
          `${bin.depthM.toFixed(1)}m<br>${bin.result.totalNm.toFixed(2)}nm</div>`;
        L.marker([lat, lng], {
          icon: L.divIcon({ html, className: "", iconSize: [52, 22], iconAnchor: [26, 11] }),
          interactive: false,
          zIndexOffset: 500,
        }).addTo(statLabelLayer);
      }
    }
  }

  async function computeStatForAOI(aoi) {
    if (aoi.computing) return;
    aoi.computing = true;
    statComputing = true;
    aoi.bins.forEach(b => b.layer?.remove());
    aoi.bins = [];
    aoi.status = "Building grid\u2026";
    render();

    try {
      let workingPoly = aoi.poly;
      if (statUseEnc) {
        aoi.status = "Fetching ENC depth areas\u2026";
        render();
        const depare = await fetchEncDepareForAOI(aoi.poly);
        if (depare.length) {
          const { bins: encBins, coverage } = buildEncShallowBins(aoi.poly, depare);
          if (encBins.length) {
            addStatBinsToMap(encBins);
            encBins.forEach(colorStatBin);
            aoi.bins.push(...encBins);
            render();
            try { workingPoly = turf.difference(turf.featureCollection([aoi.poly, coverage])); }
            catch { /* keep workingPoly = aoi.poly on geometry failure */ }
          }
        }
      }

      if (workingPoly) {
        aoi.status = "Pass 1 \u2014 coarse 4000 m grid";
        const coarseBins = createStatBins(workingPoly, 4000);
        addStatBinsToMap(coarseBins);
        aoi.bins.push(...coarseBins);
        render();
        await sampleStatWaves(coarseBins, "Pass 1 of 6", aoi);

        let passParents = coarseBins;
        for (let p = 0; p < STAT_REFINE_LEVELS.length; p++) {
          const { fromSizeM, toSizeM, belowDepthM } = STAT_REFINE_LEVELS[p];
          const toRefine = passParents.filter(b => b.depthM != null && b.depthM < belowDepthM && b.sizeM === fromSizeM);
          if (!toRefine.length) { passParents = []; continue; }

          aoi.status = `Pass ${p + 2} of 6 \u2014 ${toRefine.length} \xd7 ${fromSizeM}m \u2192 ${toSizeM}m`;
          render();

          const newBins = [];
          for (const parent of toRefine) {
            parent.layer?.remove();
            const idx = aoi.bins.indexOf(parent);
            if (idx >= 0) aoi.bins.splice(idx, 1);
            const children = createStatBins(parent.poly, toSizeM);
            children.forEach(c => { c.maxDepthM = belowDepthM * 1.15; c.parentDepth = parent.depthM; });
            addStatBinsToMap(children);
            aoi.bins.push(...children);
            newBins.push(...children);
          }

          render();
          await sampleStatWaves(newBins, `Pass ${p + 2} of 6`, aoi);

          for (const child of newBins) {
            if (child.depthM == null && !child.isLand && child.parentDepth != null) {
              child.depthM = child.parentDepth;
              child.result = computeStatRow(child.areaAcres, child.parentDepth, statCovXwd, statCrosslinePct, statShoalPct, statRunInM);
              colorStatBin(child);
            }
          }
          render();
          passParents = newBins;
        }
      }

      for (const bin of aoi.bins) {
        if (bin.depthM == null && bin.layer) bin.layer.setStyle({ fillOpacity: 0, opacity: 0 });
      }

      if (statShowContours) { aoi.status = "Drawing contours\u2026"; render(); await generateStatContours(); }

      const sampled = aoi.bins.filter(b => b.depthM != null).length;
      aoi.status = `\u2713 ${sampled} bins sampled`;
    } catch (e) {
      aoi.status = `Error: ${e?.message || e}`;
      console.error("[StatLNM]", e);
    }
    aoi.computing = false;
    statComputing = statAOIs.some(a => a.computing);
    updateStatLabels();
    render();
  }


  function recomputeAllStat() {
    for (const aoi of statAOIs) {
      for (const bin of aoi.bins) {
        if (bin.depthM != null) {
          bin.result = computeStatRow(bin.areaAcres, bin.depthM, statCovXwd, statCrosslinePct, statShoalPct, statRunInM);
          colorStatBin(bin);
        }
      }
    }
    updateStatLabels();
    render();
  }

  function startStatDraw() {
    if (statDrawing || statComputing) return;
    statDrawing = true;
    setStatus("Click vertices — double-click to finish");
    render();
    const was = map.doubleClickZoom?.enabled?.() ?? false;
    map.doubleClickZoom?.disable?.();
    if (mapEl) mapEl.style.cursor = "crosshair";
    const pts = [];
    const temp = L.polygon([], { color: "#f59e0b", weight: 2, opacity: 0.8, fillOpacity: 0.06, dashArray: "6 4" }).addTo(statLayer);
    const finish = (cancelled) => {
      map.off("click", _statDrawHandlers.click);
      map.off("dblclick", _statDrawHandlers.dbl);
      _statDrawHandlers = null;
      temp.remove();
      if (was) setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
      if (mapEl) mapEl.style.cursor = "";
      statDrawing = false;
      if (cancelled || pts.length < 3) { setStatus(cancelled ? "" : "Need ≥ 3 vertices"); render(); return; }
      const ring = pts.map(p => [p.lng, p.lat]); ring.push([pts[0].lng, pts[0].lat]);
      try {
        const poly = turf.polygon([ring]);
        const drawnCount = statAOIs.filter(a => a.source === "draw").length + 1;
        const aoi = addStatAOI(`Drawn ${drawnCount}`, poly, "draw");
        computeStatForAOI(aoi);
      } catch { setStatus("Invalid polygon"); render(); }
    };
    _statDrawHandlers = {
      click: (e) => { pts.push(e.latlng); temp.setLatLngs(pts); },
      dbl: (e) => { L.DomEvent.stop(e); finish(false); },
    };
    map.on("click", _statDrawHandlers.click);
    map.on("dblclick", _statDrawHandlers.dbl);
  }

  function cancelStatDraw() {
    if (!statDrawing || !_statDrawHandlers) return;
    map.off("click", _statDrawHandlers.click);
    map.off("dblclick", _statDrawHandlers.dbl);
    _statDrawHandlers = null;
    if (mapEl) mapEl.style.cursor = "";
    map.doubleClickZoom?.enable?.();
    statDrawing = false;
    setStatus(""); render();
  }

  function clearAllStatAOIs() {
    cancelStatDraw();
    statAOIs.forEach(aoi => { aoi.bins.forEach(b => b.layer?.remove()); aoi.outlineLayer?.remove(); });
    statAOIs = [];
    statContourLayer.clearLayers();
    statLabelLayer.clearLayers();
    setStatus(""); render();
  }

  // ── File import helpers ───────────────────────────────────────────────────────

  function extractAllPolygonsFromKML(kmlText, baseName) {
    const doc = new DOMParser().parseFromString(kmlText, "text/xml");
    const results = [];
    for (const pm of doc.querySelectorAll("Placemark")) {
      const pmName = pm.querySelector("name")?.textContent?.trim() || null;
      for (const polyEl of pm.querySelectorAll("Polygon")) {
        const outerEl = polyEl.querySelector("outerBoundaryIs coordinates") || polyEl.querySelector("LinearRing coordinates") || polyEl.querySelector("coordinates");
        if (!outerEl) continue;
        const coords = parseKMLCoords(outerEl.textContent || "");
        if (coords.length >= 3) results.push({ name: pmName || baseName || "AOI", ring: [...coords, coords[0]] });
      }
    }
    if (!results.length) {
      for (const el of doc.querySelectorAll("coordinates")) {
        const coords = parseKMLCoords(el.textContent || "");
        if (coords.length >= 3) { results.push({ name: baseName || "AOI", ring: [...coords, coords[0]] }); break; }
      }
    }
    return results;
  }

  function extractAllPolygonsFromGeoJSON(gj, baseName) {
    const candidates = gj.type === "FeatureCollection" ? (gj.features || []) : [gj];
    const results = [];
    for (const item of candidates) {
      const name = item?.properties?.name || item?.properties?.Name || baseName || "AOI";
      const geom = item?.geometry || item;
      if (geom?.type === "Polygon" && Array.isArray(geom.coordinates?.[0])) {
        const ring = geom.coordinates[0];
        if (ring.length >= 3) results.push({ name, ring: [...ring, ring[0]] });
      } else if (geom?.type === "MultiPolygon") {
        (geom.coordinates || []).forEach((poly, i) => {
          const ring = poly?.[0];
          if (ring?.length >= 3) results.push({ name: results.length === 0 ? name : `${name} ${i + 1}`, ring: [...ring, ring[0]] });
        });
      }
    }
    return results;
  }

  async function importStatFile(file) {
    if (!file) return;
    const fname = String(file.name || "").toLowerCase();
    const baseName = file.name.replace(/\.[^.]+$/, "");
    let polygons = [];
    try {
      if (fname.endsWith(".kmz")) {
        const JSZip = (await import("jszip")).default;
        const zip = await JSZip.loadAsync(file);
        const kmlEntry = Object.values(zip.files).find(f => f.name.toLowerCase().endsWith(".kml"));
        if (!kmlEntry) throw new Error("No KML inside KMZ.");
        polygons = extractAllPolygonsFromKML(await kmlEntry.async("text"), baseName);
      } else if (fname.endsWith(".kml")) {
        polygons = extractAllPolygonsFromKML(await file.text(), baseName);
      } else if (fname.endsWith(".geojson") || fname.endsWith(".json")) {
        polygons = extractAllPolygonsFromGeoJSON(JSON.parse(await file.text()), baseName);
      } else {
        app.ui.toast("Use KMZ, KML, or GeoJSON."); return;
      }
      if (!polygons.length) { app.ui.toast("No valid polygons found."); return; }
      let added = 0;
      for (const { name, ring } of polygons) {
        if (ring.length < 4) continue;
        try {
          const poly = turf.polygon([ring]);
          const aoi = addStatAOI(name, poly, "file");
          computeStatForAOI(aoi);
          added++;
        } catch {}
      }
      if (added) app.ui.toast(`Added ${added} AOI${added !== 1 ? "s" : ""} from ${file.name}`);
      else app.ui.toast("No valid polygons imported.");
    } catch (err) {
      app.ui.toast(`Import failed: ${err?.message || "error"}`);
    }
  }

  async function loadStatDamFiles() {
    const projectId = String(app.services?.project?.getProjectId?.() || "").trim();
    if (!projectId) { app.ui.toast("Select a project first"); return; }
    statDamLoading = true; statDamOpen = true; render();
    try {
      const res = await fetch(`/api/project/assets?code=${encodeURIComponent(projectId)}&category=vector-files`);
      const data = await res.json();
      statDamFiles = data.ok ? (data.files || []) : [];
    } catch { statDamFiles = []; }
    statDamLoading = false; render();
  }

  async function openStatDamFile(key, name) {
    statDamOpen = false; render();
    try {
      const pr = await fetch(`/api/s3/presign/download?key=${encodeURIComponent(key)}`);
      const { url } = await pr.json();
      const blob = await fetch(url).then(r => r.blob());
      await importStatFile(new File([blob], name));
    } catch (err) {
      app.ui.toast(`DAM load failed: ${err?.message || "error"}`);
    }
  }

  // ── Multi-AOI table ───────────────────────────────────────────────────────────

  function buildStatHtml() {
    const f1 = v => Number.isFinite(v) ? v.toFixed(1) : "—";
    const f2 = v => Number.isFinite(v) ? v.toFixed(2) : "—";
    const td  = "padding:2px 5px;text-align:right;white-space:nowrap;font-size:10px;";
    const tdL = `${td}text-align:left;`;
    const th  = `${td}font-weight:600;color:var(--muted);border-bottom:1px solid var(--border);font-size:9px;`;
    const thL = `${th}text-align:left;`;

    let grandAcres = 0, grandMain = 0, grandXl = 0, grandTotal = 0, grandCost = 0;
    let aoiHtml = "";

    for (const aoi of statAOIs) {
      const groups = groupAOIBins(aoi);
      let aoiAcres = 0, aoiMain = 0, aoiXl = 0, aoiTotal = 0, aoiCost = 0;
      groups.forEach(g => { aoiAcres += g.totalAcres; aoiMain += g.totalMainNm; aoiXl += g.totalXlNm; aoiTotal += g.totalNm; aoiCost += g.totalNm * (Number(statCostRates[g.label]) || 0); });
      grandAcres += aoiAcres; grandMain += aoiMain; grandXl += aoiXl; grandTotal += aoiTotal; grandCost += aoiCost;

      const headerStatus = aoi.computing
        ? `<span style="font-size:10px;color:#a78bfa;flex-shrink:0;">${esc(aoi.status || "computing…")}</span>`
        : aoiTotal > 0
          ? `<span style="font-size:10px;color:var(--muted);flex-shrink:0;">${f1(aoiAcres)} ac · ${f2(aoiTotal)} nm${aoiCost > 0 ? " · $" + Math.round(aoiCost).toLocaleString() : ""}</span>`
          : `<span style="font-size:10px;color:var(--muted);flex-shrink:0;">${esc(aoi.status || "not computed")}</span>`;

      let bodyHtml = "";
      if (!aoi.collapsed && groups.length) {
        const rows = groups.map(g => {
          const avgD = g.bins.reduce((s,b) => s+b.depthM,0)/g.bins.length;
          const avgSw = g.bins.reduce((s,b) => s+(b.result?.swathM??0),0)/g.bins.length;
          const gRate = Number(statCostRates[g.label]) || 0;
          const gCost = g.totalNm * gRate;
          return `<tr style="border-bottom:1px solid rgba(148,163,184,0.07);">
            <td style="${tdL}"><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${g.color};margin-right:4px;vertical-align:middle;"></span>${esc(g.label)}</td>
            <td style="${td}">${g.bins.length}</td><td style="${td}">${f1(g.totalAcres)}</td>
            <td style="${td}">${f1(avgD)}</td><td style="${td}">${Math.round(avgSw)||"—"}</td>
            <td style="${td}">${f2(g.totalMainNm)}</td><td style="${td}">${f2(g.totalXlNm)}</td>
            <td style="${td};font-weight:600;">${f2(g.totalNm)}</td>
            <td style="${td};color:${gCost>0?"#34d399":"var(--muted)"};">${gCost > 0 ? "$" + Math.round(gCost).toLocaleString() : "—"}</td></tr>`;
        }).join("");
        const subRow = `<tr style="border-top:1px solid var(--border);font-weight:700;font-size:10px;">
          <td style="${tdL}">Subtotal</td><td style="${td}"></td><td style="${td}">${f1(aoiAcres)}</td>
          <td style="${td}"></td><td style="${td}"></td>
          <td style="${td}">${f2(aoiMain)}</td><td style="${td}">${f2(aoiXl)}</td><td style="${td}">${f2(aoiTotal)}</td>
          <td style="${td}">${aoiCost > 0 ? "$" + Math.round(aoiCost).toLocaleString() : "—"}</td></tr>`;
        bodyHtml = `<div style="overflow-x:auto;padding:2px 4px 4px;">
          <table style="width:100%;border-collapse:collapse;">
            <thead><tr>
              <th style="${thL}">Range</th><th style="${th}">Bins</th><th style="${th}">Acres</th>
              <th style="${th}">Avg m</th><th style="${th}">Swath m</th>
              <th style="${th}">Main nm</th><th style="${th}">XL nm</th><th style="${th}">Total nm</th><th style="${th}">Cost</th>
            </tr></thead>
            <tbody>${rows}</tbody><tfoot>${subRow}</tfoot>
          </table></div>`;
      } else if (!aoi.collapsed && !groups.length && !aoi.computing) {
        bodyHtml = `<div style="padding:4px 8px;font-size:10px;color:var(--muted);">Click ↻ to compute depth bins.</div>`;
      }

      aoiHtml += `<div style="border:1px solid var(--border);border-radius:4px;margin-bottom:5px;overflow:hidden;">
        <div style="display:flex;align-items:center;gap:5px;padding:4px 6px;background:rgba(148,163,184,0.04);cursor:pointer;user-select:none;" data-stat-toggle="${esc(aoi.id)}">
          <span style="font-size:9px;color:var(--muted);">${aoi.collapsed ? "▶" : "▾"}</span>
          <span style="font-size:11px;font-weight:600;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${esc(aoi.name)}">${esc(aoi.name)}</span>
          ${headerStatus}
          ${!aoi.computing ? `<button data-stat-recompute="${esc(aoi.id)}" class="btn" style="font-size:9px;padding:1px 5px;margin-left:3px;" title="Recompute">↻</button>` : ""}
          <button data-stat-remove="${esc(aoi.id)}" class="btn" style="font-size:9px;padding:1px 5px;color:#f87171;border-color:#f8717144;" title="Remove">×</button>
        </div>
        ${bodyHtml}
      </div>`;
    }

    const grandRow = statAOIs.length > 1 && grandTotal > 0
      ? `<div style="border-top:2px solid var(--border);padding:4px 8px;display:flex;justify-content:space-between;font-size:11px;font-weight:700;margin-top:4px;">
          <span>Grand Total</span>
          <span>${f1(grandAcres)} ac · ${f2(grandMain)} main · ${f2(grandTotal)} nm${grandCost > 0 ? " · $" + Math.round(grandCost).toLocaleString() : ""}</span>
        </div>` : "";

    const emptyMsg = !statAOIs.length
      ? `<div class="small" style="color:var(--muted);margin-top:6px;">Draw an AOI or load from file — bins auto-grid by depth zone.</div>`
      : "";

    let damPickerHtml = "";
    if (statDamOpen) {
      const items = statDamLoading
        ? `<div style="padding:6px;font-size:11px;color:var(--muted);">Loading…</div>`
        : statDamFiles.length
          ? statDamFiles.map(f => `<div data-stat-dam-key="${esc(f.key)}" data-stat-dam-name="${esc(f.name)}" style="padding:4px 8px;font-size:11px;cursor:pointer;border-bottom:1px solid rgba(148,163,184,0.1);">📄 ${esc(f.name)}</div>`).join("")
          : `<div style="padding:6px;font-size:11px;color:var(--muted);">No vector files in DAM for this project.</div>`;
      damPickerHtml = `<div id="mbslp-stat-dam-picker" style="border:1px solid var(--border);border-radius:4px;margin-bottom:6px;max-height:140px;overflow-y:auto;">${items}</div>`;
    }

    return `
  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:4px;">Statistical LNM Estimator</div>
    <div class="small" style="color:var(--muted);font-size:10px;margin-bottom:8px;">ENC depth areas &lt;20m + adaptive grid sampling ≥ 20m → LNM via √area method.</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px 12px;font-size:11px;margin-bottom:8px;">
      <label style="display:flex;flex-direction:column;gap:2px;">Sector coverage (\xd7WD)
        <input id="mbslp-stat-cov" type="number" value="${statCovXwd}" min="1" max="20" step="0.5" style="width:70px;" ${statComputing ? "disabled" : ""} />
      </label>
      <label style="display:flex;flex-direction:column;gap:2px;">Crossline %
        <input id="mbslp-stat-xl" type="number" value="${statCrosslinePct}" min="0" max="100" step="1" style="width:70px;" ${statComputing ? "disabled" : ""} />
      </label>
      <label style="display:flex;flex-direction:column;gap:2px;">Shoal contingency %
        <input id="mbslp-stat-shoal" type="number" value="${statShoalPct}" min="0" max="100" step="1" style="width:70px;" ${statComputing ? "disabled" : ""} />
      </label>
      <label style="display:flex;flex-direction:column;gap:2px;">Run-in buffer (m)
        <input id="mbslp-stat-runin" type="number" value="${statRunInM}" min="0" max="2000" step="50" style="width:70px;" ${statComputing ? "disabled" : ""} />
      </label>
      <label style="display:flex;flex-direction:column;gap:2px;">Depth source
        <select id="mbslp-stat-src" ${statComputing ? "disabled" : ""}>
          <option value="etopo"${statDepthSrc === "etopo" ? " selected" : ""}>ETOPO (NGDC)</option>
          <option value="gebco"${statDepthSrc === "gebco" ? " selected" : ""}>GEBCO (ArcGIS)</option>
        </select>
      </label>
      <label style="display:flex;flex-direction:row;gap:6px;align-items:center;padding-top:16px;cursor:pointer;">
        <input id="mbslp-stat-enc" type="checkbox" ${statUseEnc ? "checked" : ""} ${statComputing ? "disabled" : ""} />
        <span>ENC depth areas (&lt;20m)</span>
      </label>
      <label style="display:flex;flex-direction:row;gap:6px;align-items:center;padding-top:16px;cursor:pointer;">
        <input id="mbslp-stat-contours" type="checkbox" ${statShowContours ? "checked" : ""} />
        <span>Depth contours</span>
      </label>
      <label style="display:flex;flex-direction:row;gap:6px;align-items:center;padding-top:16px;cursor:pointer;">
        <input id="mbslp-stat-labels" type="checkbox" ${statShowLabels ? "checked" : ""} />
        <span>Cell LNM labels</span>
      </label>
    </div>
    <div style="margin-bottom:8px;padding:6px 8px;border:1px solid var(--border);border-radius:4px;">
      <div style="font-size:9px;font-weight:600;color:var(--muted);letter-spacing:.05em;margin-bottom:5px;">PRICING ($/LNM)</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:3px 10px;">
        ${STAT_RANGES.map(r => `<label style="display:flex;align-items:center;gap:4px;font-size:10px;">
          <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${r.color};flex-shrink:0;"></span>
          <span style="min-width:58px;color:var(--muted);white-space:nowrap;">${esc(r.label)}</span>
          <input data-cost-range="${esc(r.label)}" type="number" min="0" step="500" value="${Number(statCostRates[r.label]) || ""}" placeholder="0" style="width:62px;font-size:10px;" />
        </label>`).join("")}
      </div>
    </div>
    <div class="row" style="gap:5px;flex-wrap:wrap;margin-bottom:6px;">
      <button id="mbslp-stat-draw" class="btn${statDrawing ? " primary" : ""}" style="font-size:11px;" ${statComputing ? "disabled" : ""}>
        ${statDrawing ? "⏹ Cancel" : "✏ Draw AOI"}
      </button>
      <label class="btn" style="font-size:11px;cursor:pointer;" title="Load KMZ, KML, or GeoJSON">
        📂 File
        <input type="file" id="mbslp-stat-file" accept=".kmz,.kml,.geojson,.json" style="display:none;" multiple />
      </label>
      <button id="mbslp-stat-dam" class="btn" style="font-size:11px;">↓ DAM</button>
      ${statAOIs.length ? `<button id="mbslp-stat-clear-all" class="btn" style="font-size:11px;color:#f87171;border-color:#f8717144;">× Clear all</button>` : ""}
      ${statAOIs.some(a => a.bins.some(b => b.result)) ? `<button id="mbslp-stat-print" class="btn" style="font-size:11px;" title="Print PDF report">🖨 Report</button>` : ""}
    </div>
    ${damPickerHtml}
    ${emptyMsg}
    <div id="mbslp-stat-aoi-list">${aoiHtml}</div>
    ${grandRow}
  </div>`;
  }



  // ── Print Report ─────────────────────────────────────────────────────────────

  async function printReport() {
    const hasStatData = statAOIs.some(a => a.bins.some(b => b.result));
    const hasLineData = lines.length > 0 && stats;
    if (!hasStatData && !hasLineData) { app.ui.toast("No data to report — generate lines or run the LNM estimator first."); return; }

    // Load html2canvas from CDN if not already present
    if (!window.html2canvas) {
      app.ui.toast("Loading map capture…");
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js";
        s.onload = resolve;
        s.onerror = () => reject(new Error("Failed to load html2canvas"));
        document.head.appendChild(s);
      });
    }

    app.ui.toast("Capturing map…");
    let mapImgSrc = "";
    try {
      const canvas = await window.html2canvas(mapEl, { useCORS: true, allowTaint: false, logging: false, backgroundColor: "#0f172a" });
      mapImgSrc = canvas.toDataURL("image/png");
    } catch (e) { console.warn("[MBSwathPlanner] map capture:", e); }

    // Aggregate LNM+cost by depth band across all AOIs
    const bandTotals = {};
    for (const r of STAT_RANGES) bandTotals[r.label] = { ...r, totalNm: 0, totalAcres: 0, totalMainNm: 0, totalXlNm: 0, bins: 0 };
    for (const aoi of statAOIs) {
      for (const g of groupAOIBins(aoi)) {
        const b = bandTotals[g.label]; if (!b) continue;
        b.totalNm += g.totalNm; b.totalAcres += g.totalAcres;
        b.totalMainNm += g.totalMainNm; b.totalXlNm += g.totalXlNm; b.bins += g.bins.length;
      }
    }
    const activeBands = Object.values(bandTotals).filter(b => b.bins > 0);
    let grandNm = 0, grandCostR = 0, grandAcresR = 0;
    activeBands.forEach(b => { b.rate = Number(statCostRates[b.label]) || 0; b.cost = b.totalNm * b.rate; grandNm += b.totalNm; grandCostR += b.cost; grandAcresR += b.totalAcres; });
    const hasCost = activeBands.some(b => b.rate > 0);
    const f2r = v => Number.isFinite(v) ? v.toFixed(2) : "—";
    const fc = v => v > 0 ? "$" + Math.round(v).toLocaleString() : "—";
    const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const projectId = String(app.services?.project?.getProjectId?.() || "").trim();

    const lnmRows = activeBands.map(b =>
      `<tr><td><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${b.color};margin-right:6px;vertical-align:middle;"></span>${b.label}</td>
       <td>${b.bins}</td><td>${b.totalAcres.toFixed(1)}</td>
       <td>${f2r(b.totalMainNm)}</td><td>${f2r(b.totalXlNm)}</td><td>${f2r(b.totalNm)}</td>
       ${hasCost ? "<td>" + (b.rate > 0 ? "$" + b.rate.toLocaleString() : "—") + "</td><td>" + fc(b.cost) + "</td>" : ""}</tr>`
    ).join("");
    const lnmTotalRow = `<tr class="tot"><td><strong>Total</strong></td><td></td><td>${grandAcresR.toFixed(1)}</td>
      <td></td><td></td><td><strong>${f2r(grandNm)}</strong></td>
      ${hasCost ? "<td></td><td><strong>" + fc(grandCostR) + "</strong></td>" : ""}</tr>`;

    const aoiRows = statAOIs.length > 1 ? statAOIs.map(aoi => {
      const gs = groupAOIBins(aoi);
      const aoiNm = gs.reduce((s,g) => s+g.totalNm, 0);
      const aoiAc = gs.reduce((s,g) => s+g.totalAcres, 0);
      const aoiCostVal = gs.reduce((s,g) => s + g.totalNm * (Number(statCostRates[g.label])||0), 0);
      return `<tr><td>${aoi.name}</td><td>${aoiAc.toFixed(1)}</td><td>${f2r(aoiNm)}</td>${hasCost ? "<td>" + fc(aoiCostVal) + "</td>" : ""}</tr>`;
    }).join("") : "";

    const lineStatsHtml = hasLineData ? `
      <h2>Generated Survey Lines</h2>
      <table><thead><tr><th>Metric</th><th>Value</th></tr></thead><tbody>
        <tr><td>Lines generated</td><td>${stats.count}</td></tr>
        <tr><td>Total length</td><td>${stats.totalKm} km</td></tr>
        <tr><td>Avg line spacing</td><td>${stats.avgSpacingM} m</td></tr>
        <tr><td>Min / Max spacing</td><td>${stats.minSpacingM} – ${stats.maxSpacingM} m</td></tr>
        <tr><td>Depth range (sampled)</td><td>${stats.minDepthM} – ${stats.maxDepthM} m</td></tr>
        <tr><td>Mode</td><td>${state.mode}</td></tr>
        <tr><td>Swath angle</td><td>${state.swathAngle}°</td></tr>
        <tr><td>Overlap</td><td>${state.overlap}%</td></tr>
      </tbody></table>` : "";

    const html = `<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>MBES Swath Planner Report${projectId ? " – " + projectId : ""}</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;margin:0;padding:24px 32px;}
        h1{font-size:20px;margin:0 0 4px;}
        h2{font-size:14px;margin:24px 0 6px;padding-bottom:3px;border-bottom:2px solid #0ea5e9;color:#0f172a;}
        .meta{color:#64748b;font-size:11px;margin-bottom:6px;}
        .params{display:flex;gap:16px;flex-wrap:wrap;font-size:11px;color:#475569;margin-bottom:16px;padding:6px 10px;background:#f8fafc;border-radius:4px;}
        .params strong{color:#1e293b;}
        table{width:100%;border-collapse:collapse;margin-top:4px;}
        th{background:#f1f5f9;text-align:left;padding:6px 8px;font-size:11px;color:#475569;border-bottom:2px solid #cbd5e1;}
        td{padding:5px 8px;border-bottom:1px solid #e2e8f0;font-size:11px;}
        tr.tot td{border-top:2px solid #cbd5e1;font-weight:700;border-bottom:none;}
        img{width:100%;border:1px solid #e2e8f0;border-radius:4px;}
        .footer{margin-top:32px;text-align:right;font-size:10px;color:#94a3b8;}
        @media print{button{display:none;}}
      </style>
    </head><body>
      <h1>MBES Swath Planner Report</h1>
      <div class="meta">${projectId ? "Project: <strong>" + projectId + "</strong> &nbsp;·&nbsp; " : ""}Generated: ${now}</div>
      <div class="params">
        <span>Coverage: <strong>${statCovXwd}×WD</strong></span>
        <span>Crossline: <strong>${statCrosslinePct}%</strong></span>
        <span>Shoal contingency: <strong>${statShoalPct}%</strong></span>
        <span>Run-in: <strong>${statRunInM} m</strong></span>
        ${statAOIs.length ? "<span>AOIs: <strong>" + statAOIs.length + "</strong></span>" : ""}
      </div>
      <h2>Survey Map</h2>
      ${mapImgSrc ? `<img src="${mapImgSrc}" />` : `<div style="padding:40px;background:#f1f5f9;text-align:center;color:#94a3b8;border-radius:4px;">Map capture unavailable</div>`}
      ${hasStatData ? `
      <h2>LNM Summary by Depth Band</h2>
      <table><thead><tr>
        <th>Depth Range</th><th>Bins</th><th>Area (ac)</th>
        <th>Main nm</th><th>XL nm</th><th>Total nm</th>
        ${hasCost ? "<th>Rate ($/nm)</th><th>Cost</th>" : ""}
      </tr></thead><tbody>${lnmRows}${lnmTotalRow}</tbody></table>
      ${statAOIs.length > 1 ? `
      <h2>By AOI</h2>
      <table><thead><tr><th>AOI</th><th>Area (ac)</th><th>Total nm</th>${hasCost ? "<th>Cost</th>" : ""}</tr></thead>
      <tbody>${aoiRows}</tbody></table>` : ""}` : ""}
      ${lineStatsHtml}
      <div class="footer">AOS HydroTools &nbsp;·&nbsp; MBES Swath Planner</div>
      <script>setTimeout(function(){window.print();},400);<\/script>
    </body></html>`;

    const win = window.open("", "_blank");
    if (!win) { app.ui.toast("Pop-up blocked — allow pop-ups and try again."); return; }
    win.document.open(); win.document.write(html); win.document.close();
  }

  function persist() { persistState(state); }
  function setStatus(s) {
    status = s;
    const el = containerEl?.querySelector?.("#mbslp-status");
    if (el) el.textContent = s;
  }

  // ── Map display ───────────────────────────────────────────────────────────────

  function renderBoundaryOnMap() {
    boundaryLayer.clearLayers();
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) return;
    L.polygon(state.boundaryCoords.map(([lng, lat]) => [lat, lng]), {
      color: "#f59e0b", weight: 2, fillColor: "#f59e0b", fillOpacity: 0.06, dashArray: "6 4", interactive: false,
    }).addTo(boundaryLayer);
  }

  function renderSpineOnMap() {
    spineLayer.clearLayers();
    if (!state.spineCoords || state.spineCoords.length < 2) return;
    const ll = state.spineCoords.map(([lng, lat]) => [lat, lng]);
    L.polyline(ll, { color: "#a78bfa", weight: 2.5, dashArray: "8 4", interactive: false }).addTo(spineLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#a78bfa", fillColor: "#a78bfa", fillOpacity: 1, interactive: false }).addTo(spineLayer));
  }

  function renderSectionOnMap() {
    sectionLayer.clearLayers();
    if (!state.sectionCoords || state.sectionCoords.length < 2) return;
    const ll = state.sectionCoords.map(([lng, lat]) => [lat, lng]);
    L.polyline(ll, { color: "#fb7185", weight: 2.5, dashArray: "8 4", interactive: false }).addTo(sectionLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#fb7185", fillColor: "#fb7185", fillOpacity: 1, interactive: false }).addTo(sectionLayer));
  }

  function renderLinesOnMap() {
    linesLayer.clearLayers();
    lines.forEach((f) => {
      const coords = f.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
      const p = f.properties;
      L.polyline(coords, { color: "#22d3ee", weight: 1.8, opacity: 0.85, interactive: false })
        .addTo(linesLayer)
        .bindTooltip(`${p.name} | d:${p.waterDepthM}m swath:${p.swathWidthM}m spacing:${p.spacingM}m`,
          { permanent: false, direction: "top", sticky: true, opacity: 0.9 });
    });
  }

  // ── Boundary draw ─────────────────────────────────────────────────────────────

  function drawKeyHandler(e) {
    if (e.key === "Escape") stopDraw("cancelled");
    if (e.key === "Enter") finishDraw();
  }
  function updateDrawPreview(mouseLatLng) {
    drawLayer.clearLayers();
    const pts = mouseLatLng ? [...drawVertices, [mouseLatLng.lng, mouseLatLng.lat]] : drawVertices;
    if (!pts.length) return;
    const ll = pts.map(([lng, lat]) => [lat, lng]);
    if (pts.length >= 2) L.polyline(ll, { color: "#f59e0b", weight: 2, dashArray: "5 5", interactive: false }).addTo(drawLayer);
    if (pts.length >= 3) L.polygon(ll, { color: "#f59e0b", weight: 1, fillOpacity: 0.08, interactive: false }).addTo(drawLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#f59e0b", fillOpacity: 1, interactive: false }).addTo(drawLayer));
  }
  function finishDraw() {
    if (drawVertices.length < 3) { app.ui.toast("Need at least 3 points."); return; }
    state.boundaryCoords = [...drawVertices];
    lines = []; stats = null; waterGrid = null;
    contourLayer.clearLayers(); contourDrawn = false;
    persist(); stopDraw("completed"); renderBoundaryOnMap();
    app.ui.toast(`Boundary set — ${state.boundaryCoords.length} vertices.`);
  }
  function stopDraw(reason) {
    drawingBoundary = false;
    drawHandlers.forEach(([evt, fn]) => { try { map.off(evt, fn); } catch {} });
    document.removeEventListener("keydown", drawKeyHandler);
    drawHandlers = []; drawLayer.clearLayers(); drawVertices = [];
    if (mapEl) mapEl.style.cursor = "";
    render();
  }
  function startDrawBoundary() {
    if (drawingBoundary) { finishDraw(); return; }
    if (drawingAzimuth) stopDrawAzimuth("cancelled");
    if (drawingSpine) stopSpineDraw("cancelled");
    if (drawingSection) stopSectionDraw("cancelled");
    state.boundaryCoords = null; lines = []; stats = null; status = "";
    boundaryLayer.clearLayers(); linesLayer.clearLayers();
    drawingBoundary = true; drawVertices = [];
    if (mapEl) mapEl.style.cursor = "crosshair";
    render();
    app.ui.toast("Click to add vertices · Right-click or Enter to finish · Esc to cancel");
    const onClick = (e) => {
      const last = drawVertices[drawVertices.length - 1];
      if (last && Math.abs(last[0] - e.latlng.lng) < 1e-9 && Math.abs(last[1] - e.latlng.lat) < 1e-9) return;
      drawVertices.push([e.latlng.lng, e.latlng.lat]);
      updateDrawPreview(null);
      const el = containerEl?.querySelector?.("#mbslp-draw-count");
      if (el) el.textContent = `${drawVertices.length} pts`;
    };
    const onMove = (e) => updateDrawPreview(e.latlng);
    const onDbl = (e) => { L.DomEvent.stop(e); finishDraw(); };
    const onCtx = (e) => { L.DomEvent.stop(e); finishDraw(); };
    map.on("click", onClick); map.on("mousemove", onMove); map.on("dblclick", onDbl); map.on("contextmenu", onCtx);
    document.addEventListener("keydown", drawKeyHandler);
    drawHandlers = [["click", onClick], ["mousemove", onMove], ["dblclick", onDbl], ["contextmenu", onCtx]];
  }

  // ── Azimuth draw ──────────────────────────────────────────────────────────────

  function azimuthKeyHandler(e) { if (e.key === "Escape") stopDrawAzimuth("cancelled"); }
  function updateAzimuthPreview(mouseLatLng) {
    drawLayer.clearLayers();
    if (!azimuthPt1) return;
    const pt1LL = [azimuthPt1[1], azimuthPt1[0]];
    L.polyline([pt1LL, [mouseLatLng.lat, mouseLatLng.lng]], { color: "#38bdf8", weight: 2, dashArray: "6 4", interactive: false }).addTo(drawLayer);
    L.circleMarker(pt1LL, { radius: 5, color: "#38bdf8", fillColor: "#38bdf8", fillOpacity: 1, interactive: false }).addTo(drawLayer);
    const az = Math.round(((turf.bearing(turf.point(azimuthPt1), turf.point([mouseLatLng.lng, mouseLatLng.lat])) % 360) + 360) % 360);
    const el = containerEl?.querySelector?.("#mbslp-az-live");
    if (el) el.textContent = `${az}° — click to confirm`;
  }
  function stopDrawAzimuth(reason) {
    drawingAzimuth = false; azimuthPt1 = null;
    azimuthDrawHandlers.forEach(([evt, fn]) => { try { map.off(evt, fn); } catch {} });
    document.removeEventListener("keydown", azimuthKeyHandler);
    azimuthDrawHandlers = []; drawLayer.clearLayers();
    if (mapEl) mapEl.style.cursor = "";
    render();
  }
  function startDrawAzimuth() {
    if (drawingAzimuth) { stopDrawAzimuth("cancelled"); return; }
    if (drawingBoundary) stopDraw("cancelled");
    if (drawingSpine) stopSpineDraw("cancelled");
    if (drawingSection) stopSectionDraw("cancelled");
    drawingAzimuth = true; azimuthPt1 = null;
    if (mapEl) mapEl.style.cursor = "crosshair";
    render();
    app.ui.toast("Click first point · click second point to set azimuth · Esc to cancel");
    const onClick = (e) => {
      if (!azimuthPt1) { azimuthPt1 = [e.latlng.lng, e.latlng.lat]; updateAzimuthPreview(e.latlng); }
      else {
        const bearing = turf.bearing(turf.point(azimuthPt1), turf.point([e.latlng.lng, e.latlng.lat]));
        state.azimuth = Math.round(((bearing % 360) + 360) % 360);
        persist(); stopDrawAzimuth("completed");
        app.ui.toast(`Azimuth set to ${state.azimuth}° (${azimuthLabel(state.azimuth)})`);
      }
    };
    const onMove = (e) => { if (azimuthPt1) updateAzimuthPreview(e.latlng); };
    const onCtx = (e) => { L.DomEvent.stop(e); stopDrawAzimuth("cancelled"); };
    map.on("click", onClick); map.on("mousemove", onMove); map.on("contextmenu", onCtx);
    document.addEventListener("keydown", azimuthKeyHandler);
    azimuthDrawHandlers = [["click", onClick], ["mousemove", onMove], ["contextmenu", onCtx]];
  }

  // ── Spine draw ────────────────────────────────────────────────────────────────

  function spineKeyHandler(e) {
    if (e.key === "Escape") stopSpineDraw("cancelled");
    if (e.key === "Enter") finishSpineDraw();
  }
  function updateSpinePreview(mouseLatLng) {
    drawLayer.clearLayers();
    const pts = mouseLatLng ? [...spineVertices, [mouseLatLng.lng, mouseLatLng.lat]] : spineVertices;
    if (!pts.length) return;
    const ll = pts.map(([lng, lat]) => [lat, lng]);
    if (pts.length >= 2) L.polyline(ll, { color: "#a78bfa", weight: 2.5, dashArray: "6 4", interactive: false }).addTo(drawLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#a78bfa", fillColor: "#a78bfa", fillOpacity: 1, interactive: false }).addTo(drawLayer));
  }
  function finishSpineDraw() {
    if (spineVertices.length < 2) { app.ui.toast("Need at least 2 points for a spine."); return; }
    state.spineCoords = [...spineVertices];
    lines = []; stats = null;
    persist(); stopSpineDraw("completed"); renderSpineOnMap();
    app.ui.toast(`Spine set — ${state.spineCoords.length} vertices.`);
  }
  function stopSpineDraw(reason) {
    drawingSpine = false;
    spineDrawHandlers.forEach(([evt, fn]) => { try { map.off(evt, fn); } catch {} });
    document.removeEventListener("keydown", spineKeyHandler);
    spineDrawHandlers = []; drawLayer.clearLayers(); spineVertices = [];
    if (mapEl) mapEl.style.cursor = "";
    render();
  }
  function startDrawSpine() {
    if (drawingSpine) { finishSpineDraw(); return; }
    if (drawingBoundary) stopDraw("cancelled");
    if (drawingAzimuth) stopDrawAzimuth("cancelled");
    if (drawingSection) stopSectionDraw("cancelled");
    state.spineCoords = null; lines = []; stats = null; status = "";
    spineLayer.clearLayers(); linesLayer.clearLayers();
    drawingSpine = true; spineVertices = [];
    if (mapEl) mapEl.style.cursor = "crosshair";
    render();
    app.ui.toast("Click to add spine points · Right-click or Enter to finish · Esc to cancel");
    const onClick = (e) => {
      const last = spineVertices[spineVertices.length - 1];
      if (last && Math.abs(last[0] - e.latlng.lng) < 1e-9 && Math.abs(last[1] - e.latlng.lat) < 1e-9) return;
      spineVertices.push([e.latlng.lng, e.latlng.lat]);
      updateSpinePreview(null);
      const el = containerEl?.querySelector?.("#mbslp-spine-count");
      if (el) el.textContent = `${spineVertices.length} pts`;
    };
    const onMove = (e) => updateSpinePreview(e.latlng);
    const onDbl = (e) => { L.DomEvent.stop(e); finishSpineDraw(); };
    const onCtx = (e) => { L.DomEvent.stop(e); finishSpineDraw(); };
    map.on("click", onClick); map.on("mousemove", onMove); map.on("dblclick", onDbl); map.on("contextmenu", onCtx);
    document.addEventListener("keydown", spineKeyHandler);
    spineDrawHandlers = [["click", onClick], ["mousemove", onMove], ["dblclick", onDbl], ["contextmenu", onCtx]];
  }

  // ── Section draw ──────────────────────────────────────────────────────────────

  function sectionKeyHandler(e) {
    if (e.key === "Escape") stopSectionDraw("cancelled");
    if (e.key === "Enter") finishSectionDraw();
  }
  function updateSectionPreview(mouseLatLng) {
    drawLayer.clearLayers();
    const pts = mouseLatLng ? [...sectionVertices, [mouseLatLng.lng, mouseLatLng.lat]] : sectionVertices;
    if (!pts.length) return;
    const ll = pts.map(([lng, lat]) => [lat, lng]);
    if (pts.length >= 2) L.polyline(ll, { color: "#fb7185", weight: 2.5, dashArray: "6 4", interactive: false }).addTo(drawLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#fb7185", fillColor: "#fb7185", fillOpacity: 1, interactive: false }).addTo(drawLayer));
  }
  function finishSectionDraw() {
    if (sectionVertices.length < 2) { app.ui.toast("Need at least 2 points for a section."); return; }
    state.sectionCoords = [...sectionVertices];
    lines = []; stats = null;
    persist(); stopSectionDraw("completed"); renderSectionOnMap();
    app.ui.toast(`Section set — ${state.sectionCoords.length} vertices.`);
  }
  function stopSectionDraw(reason) {
    drawingSection = false;
    sectionDrawHandlers.forEach(([evt, fn]) => { try { map.off(evt, fn); } catch {} });
    document.removeEventListener("keydown", sectionKeyHandler);
    sectionDrawHandlers = []; drawLayer.clearLayers(); sectionVertices = [];
    if (mapEl) mapEl.style.cursor = "";
    render();
  }
  function startDrawSection() {
    if (drawingSection) { finishSectionDraw(); return; }
    if (drawingBoundary) stopDraw("cancelled");
    if (drawingAzimuth) stopDrawAzimuth("cancelled");
    if (drawingSpine) stopSpineDraw("cancelled");
    state.sectionCoords = null; lines = []; stats = null; status = "";
    sectionLayer.clearLayers(); linesLayer.clearLayers();
    drawingSection = true; sectionVertices = [];
    if (mapEl) mapEl.style.cursor = "crosshair";
    render();
    app.ui.toast("Click to add section points · Right-click or Enter to finish · Esc to cancel");
    const onClick = (e) => {
      const last = sectionVertices[sectionVertices.length - 1];
      if (last && Math.abs(last[0] - e.latlng.lng) < 1e-9 && Math.abs(last[1] - e.latlng.lat) < 1e-9) return;
      sectionVertices.push([e.latlng.lng, e.latlng.lat]);
      updateSectionPreview(null);
      const el = containerEl?.querySelector?.("#mbslp-section-count");
      if (el) el.textContent = `${sectionVertices.length} pts`;
    };
    const onMove = (e) => updateSectionPreview(e.latlng);
    const onDbl = (e) => { L.DomEvent.stop(e); finishSectionDraw(); };
    const onCtx = (e) => { L.DomEvent.stop(e); finishSectionDraw(); };
    map.on("click", onClick); map.on("mousemove", onMove); map.on("dblclick", onDbl); map.on("contextmenu", onCtx);
    document.addEventListener("keydown", sectionKeyHandler);
    sectionDrawHandlers = [["click", onClick], ["mousemove", onMove], ["dblclick", onDbl], ["contextmenu", onCtx]];
  }

  // ── File import ───────────────────────────────────────────────────────────────

  function parseKMLCoords(text) {
    return (text || "").trim().split(/\s+/).reduce((acc, triplet) => {
      const parts = triplet.split(",").map(Number);
      if (parts.length >= 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1])) acc.push([parts[0], parts[1]]);
      return acc;
    }, []);
  }
  function extractCoordsFromKML(kmlText) {
    const doc = new DOMParser().parseFromString(kmlText, "text/xml");
    for (const sel of ["outerBoundaryIs coordinates", "LinearRing coordinates", "Polygon coordinates", "coordinates"]) {
      for (const el of doc.querySelectorAll(sel)) {
        const coords = parseKMLCoords(el.textContent || "");
        if (coords.length >= 3) return coords;
      }
    }
    return null;
  }
  function extractCoordsFromGeoJSON(gj) {
    const candidates = gj.type === "FeatureCollection" ? (gj.features || []) : [gj];
    for (const item of candidates) {
      const geom = item?.geometry || item;
      if (geom?.type === "Polygon" && Array.isArray(geom.coordinates?.[0]))
        return geom.coordinates[0].map(([lon, lat]) => [lon, lat]);
      if (geom?.type === "MultiPolygon" && Array.isArray(geom.coordinates?.[0]?.[0]))
        return geom.coordinates[0][0].map(([lon, lat]) => [lon, lat]);
    }
    return null;
  }
  async function importBoundaryFile(file) {
    if (!file) return;
    const name = String(file.name || "").toLowerCase();
    try {
      let coords = null;
      if (name.endsWith(".kmz")) {
        const JSZip = (await import("jszip")).default;
        const zip = await JSZip.loadAsync(file);
        const kmlEntry = Object.values(zip.files).find((f) => f.name.toLowerCase().endsWith(".kml"));
        if (!kmlEntry) throw new Error("No KML file found inside KMZ.");
        coords = extractCoordsFromKML(await kmlEntry.async("text"));
      } else if (name.endsWith(".kml")) {
        coords = extractCoordsFromKML(await file.text());
      } else if (name.endsWith(".geojson") || name.endsWith(".json")) {
        coords = extractCoordsFromGeoJSON(JSON.parse(await file.text()));
      } else {
        app.ui.toast("Unsupported format — use KMZ, KML, or GeoJSON."); return;
      }
      if (!coords || coords.length < 3) { app.ui.toast("No valid polygon found in file."); return; }
      state.boundaryCoords = coords; lines = []; stats = null; status = "";
      waterGrid = null; contourLayer.clearLayers(); contourDrawn = false;
      persist(); renderBoundaryOnMap(); linesLayer.clearLayers(); render();
      try {
        const lats = coords.map(([, lat]) => lat), lons = coords.map(([lon]) => lon);
        map.fitBounds([[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]], { padding: [40, 40] });
      } catch {}
      app.ui.toast(`Boundary loaded — ${coords.length} vertices.`);
    } catch (err) {
      app.ui.toast(`Import failed: ${err?.message || "Unknown error"}`);
      console.error("[MBSwathPlanner] importBoundaryFile failed", err);
    }
  }

  // ── Auto-orient ───────────────────────────────────────────────────────────────

  async function autoOrientFromBathymetry() {
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) { app.ui.toast("Draw a boundary first."); return; }
    setStatus("Sampling bathymetry for auto-orient…"); render();
    try {
      const closedCoords = [...state.boundaryCoords, state.boundaryCoords[0]];
      const polygon = turf.polygon([closedCoords]);
      const bbox = turf.bbox(polygon);
      const N = 10;
      const lonStep = (bbox[2] - bbox[0]) / (N - 1);
      const latStep = (bbox[3] - bbox[1]) / (N - 1);

      const gridPts = [];
      for (let j = 0; j < N; j++) {
        for (let i = 0; i < N; i++) {
          const lon = bbox[0] + i * lonStep, lat = bbox[1] + j * latStep;
          try { if (turf.booleanPointInPolygon(turf.point([lon, lat]), polygon)) gridPts.push({ i, j, lon, lat }); } catch {}
        }
      }
      if (!gridPts.length) { setStatus(""); render(); app.ui.toast("No sample points inside boundary."); return; }

      const depths = {};
      await Promise.allSettled(gridPts.map(async ({ i, j, lon, lat }) => {
        try {
          const result = await app.services.identify.sampleDepth(lon, lat, "etopo");
          const raw = Number(result?.depth_m);
          if (Number.isFinite(raw) && raw < 0) depths[`${i},${j}`] = Math.abs(raw);
        } catch {}
      }));

      const cellWidthM = lonStep * 111320, cellHeightM = latStep * 110540;
      let gxSum = 0, gySum = 0, count = 0;
      gridPts.forEach(({ i, j }) => {
        const here = depths[`${i},${j}`];
        if (here === undefined) return;
        const east = depths[`${i+1},${j}`], west = depths[`${i-1},${j}`];
        const north = depths[`${i},${j+1}`], south = depths[`${i},${j-1}`];
        if (east !== undefined && west !== undefined) { gxSum += (east - west) / (2 * cellWidthM); count++; }
        if (north !== undefined && south !== undefined) { gySum += (north - south) / (2 * cellHeightM); count++; }
      });

      if (!count) { setStatus(""); render(); app.ui.toast("Insufficient bathymetry data for auto-orient."); return; }
      const gradBearing = Math.atan2(gxSum / count, gySum / count) * (180 / Math.PI);
      state.azimuth = Math.round(((gradBearing + 90) % 360 + 360) % 360);
      persist();
      setStatus(`Auto-orient: ${state.azimuth}° (${azimuthLabel(state.azimuth)}) — along-isobath from ${count} gradient samples.`);
      render();
    } catch (err) {
      setStatus("Auto-orient failed.");
      console.error("[MBSwathPlanner] autoOrient failed", err);
    }
  }

  // ── Line generation ───────────────────────────────────────────────────────────

  async function sampleEtopo(lon, lat, minDepth) {
    let waterDepthM = minDepth, isLand = false;
    try {
      const result = await app.services.identify.sampleDepth(lon, lat, "etopo");
      const raw = Number(result?.depth_m);
      if (Number.isFinite(raw)) {
        if (raw >= 0) isLand = true;
        else waterDepthM = Math.max(minDepth, Math.abs(raw));
      }
    } catch {}
    return { waterDepthM, isLand };
  }

  async function sampleSectionProfile(segments, totalLength, sampleSpacingM, minDepth) {
    const spacing = Math.max(1, Number(sampleSpacingM) || 25);
    const sampleCount = Math.min(2000, Math.max(2, Math.floor(totalLength / spacing) + 1));
    const positions = [];
    for (let i = 0; i < sampleCount; i++) positions.push(Math.min(totalLength, i * spacing));
    if (positions[positions.length - 1] < totalLength - 0.001) positions.push(totalLength);

    const samples = [];
    for (let i = 0; i < positions.length && !generateAbort; i++) {
      const chainageM = positions[i];
      const { point, bearing } = getSpinePositionAt(segments, totalLength, chainageM);
      setStatus(`Sampling section ${i + 1}/${positions.length}…`);
      const { waterDepthM, isLand } = await sampleEtopo(point[0], point[1], minDepth);
      samples.push({ chainageM, point, bearing, waterDepthM, isLand });
    }
    return samples;
  }

  function depthAtSectionChainage(samples, chainageM, minDepth) {
    if (!samples.length) return { waterDepthM: minDepth, isLand: false };
    if (chainageM <= samples[0].chainageM) return { waterDepthM: samples[0].waterDepthM, isLand: samples[0].isLand };
    for (let i = 0; i < samples.length - 1; i++) {
      const a = samples[i], b = samples[i + 1];
      if (chainageM <= b.chainageM + 1e-6) {
        const span = Math.max(1e-6, b.chainageM - a.chainageM);
        const t = Math.max(0, Math.min(1, (chainageM - a.chainageM) / span));
        return {
          waterDepthM: Math.max(minDepth, a.waterDepthM + (b.waterDepthM - a.waterDepthM) * t),
          isLand: a.isLand && b.isLand,
        };
      }
    }
    const last = samples[samples.length - 1];
    return { waterDepthM: last.waterDepthM, isLand: last.isLand };
  }

  async function generateLines() {
    if (generating) { generateAbort = true; return; }
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) { app.ui.toast("Draw a survey boundary first."); return; }
    if (state.mode === "spine" && (!state.spineCoords || state.spineCoords.length < 2)) { app.ui.toast("Draw a spine line first."); return; }
    if (state.mode === "section" && (!state.sectionCoords || state.sectionCoords.length < 2)) { app.ui.toast("Draw a section line first."); return; }

    generating = true; generateAbort = false; lines = []; stats = null;
    linesLayer.clearLayers(); render();

    try {
      setStatus("Building minimum-depth grid…"); render();
      await buildWaterGrid();

      const closedCoords = [...state.boundaryCoords, state.boundaryCoords[0]];
      const polygon = turf.polygon([closedCoords]);
      const halfSwathRad = (state.swathAngle / 2) * Math.PI / 180;
      const overlapFrac = Math.max(0, Math.min(0.95, state.overlap / 100));
      const minDepth = Math.max(0.5, state.minDepth);
      const bbox = turf.bbox(polygon);
      const diagonalM = turf.distance(turf.point([bbox[0], bbox[1]]), turf.point([bbox[2], bbox[3]]), { units: "meters" });
      const longLineLen = diagonalM * 1.5 + 2000;

      const generated = [], depthsSampled = [], spacingsSampled = [];

      if (state.mode === "spine") {
        // Corridor mode: offset the spine outward on both sides using turf.lineOffset.
        // Lines run PARALLEL to the spine (following its curvature) — non-crossing by definition.
        const spineLine = turf.lineString(state.spineCoords);
        const defaultSpacing = Math.max(MIN_SPACING_M, 2 * minDepth * Math.tan(halfSwathRad) * (1 - overlapFrac));

        const buildSide = async (direction, startOffset) => {
          const sideLines = [];
          let cumOffset = startOffset, prevSpacing = defaultSpacing, misses = 0, dryRunM = 0;
          while (sideLines.length < MAX_LINES / 2 && !generateAbort && misses < 4) {
            let offsetLine;
            try { offsetLine = cumOffset === 0 ? spineLine : turf.lineOffset(spineLine, direction * cumOffset, { units: "meters" }); }
            catch { break; }
            const extendedCoords = extendPolylineEnds(offsetLine.geometry.coordinates, longLineLen);

            // Get ALL inside-polygon runs at this offset (handles peninsulas / inner harbours)
            const polyRuns = clipPolylineToPolygonAll(extendedCoords, polygon);
            if (polyRuns.length === 0) {
              misses++; dryRunM = 0; cumOffset += prevSpacing; continue;
            }
            misses = 0;

            let addedAny = false;
            for (const polyClipped of polyRuns) {
              const clippedRuns = waterGrid ? clipLineToDepthRuns(polyClipped, minDepth) : [polyClipped];
              for (const clippedCoords of clippedRuns) {
                if (!clippedCoords || clippedCoords.length < 2) continue;
                const lineLen = lineLengthM(clippedCoords);
                if (lineLen < MIN_LINE_LENGTH_M) continue;
                const midCoord = clippedCoords[Math.floor(clippedCoords.length / 2)];
                setStatus(`Offset ${direction >= 0 ? "+" : "−"}${Math.round(cumOffset)}m: querying depth…`);
                const { waterDepthM, isLand } = await sampleEtopo(midCoord[0], midCoord[1], minDepth);
                if (isLand) continue;
                const swathWidth = 2 * waterDepthM * Math.tan(halfSwathRad);
                const spacing = Math.max(MIN_SPACING_M, swathWidth * (1 - overlapFrac));
                prevSpacing = spacing;
                depthsSampled.push(waterDepthM); spacingsSampled.push(spacing);
                sideLines.push({ clippedCoords, waterDepthM, swathWidth, spacing, lineLen });
                addedAny = true;
              }
            }

            if (addedAny) {
              dryRunM = 0; cumOffset += prevSpacing;
            } else {
              // In polygon but all land — small step; stop if dry run exceeds polygon diagonal
              const step = Math.max(10, prevSpacing * 0.15);
              dryRunM += step; cumOffset += step;
              if (dryRunM > diagonalM) break;
            }
          }
          return sideLines;
        };

        // Right side starts at 0 (includes the spine itself), left side starts past spine
        const rightLines = await buildSide(1, 0);
        const initLeftSpacing = rightLines.length > 0 ? rightLines[0].spacing : defaultSpacing;
        const leftLines = await buildSide(-1, initLeftSpacing);

        // Order: far-left … near-left … spine … near-right … far-right
        const ordered = [...leftLines.reverse(), ...rightLines];
        ordered.forEach(({ clippedCoords, waterDepthM, swathWidth, spacing, lineLen }, i) => {
          const lineNum = i + 1;
          generated.push({
            type: "Feature",
            properties: {
              name: `L${String(lineNum).padStart(3, "0")}`, lineIndex: lineNum,
              waterDepthM: Math.round(waterDepthM * 10) / 10,
              swathWidthM: Math.round(swathWidth * 10) / 10,
              spacingM: Math.round(spacing * 10) / 10,
              lengthM: Math.round(lineLen * 10) / 10,
            },
            geometry: { type: "LineString", coordinates: clippedCoords },
          });
        });
      } else if (state.mode === "section") {
        const { segments, totalLength } = buildSpineSegments(state.sectionCoords);
        if (!segments.length || totalLength < 1) { setStatus("Section is too short."); return; }
        const samples = await sampleSectionProfile(segments, totalLength, state.sectionSampleSpacing, minDepth);
        if (generateAbort) { setStatus("Cancelled."); return; }
        const wetSamples = samples.filter((x) => !x.isLand);
        if (!wetSamples.length) { setStatus("No water depths found along section."); return; }

        let chainageM = 0;
        while (chainageM <= totalLength + 0.001 && generated.length < MAX_LINES && !generateAbort) {
          const { point, bearing } = getSpinePositionAt(segments, totalLength, chainageM);
          const { waterDepthM, isLand } = depthAtSectionChainage(samples, chainageM, minDepth);
          const swathWidth = 2 * waterDepthM * Math.tan(halfSwathRad);
          const spacing = Math.max(MIN_SPACING_M, swathWidth * (1 - overlapFrac));

          if (isLand) { chainageM += Math.max(5, spacing * 0.25); continue; }

          const lineBearing = (bearing + 90) % 360;
          const ls = turf.destination(turf.point(point), -longLineLen, lineBearing, { units: "meters" });
          const le = turf.destination(turf.point(point), longLineLen, lineBearing, { units: "meters" });
          const polyClipped = clipLineToPoly([ls.geometry.coordinates, le.geometry.coordinates], polygon);
          if (!polyClipped) { chainageM += Math.max(5, spacing * 0.25); continue; }

          const clippedRuns = waterGrid ? clipLineToDepthRuns(polyClipped, minDepth) : [polyClipped];
          let addedAtChainage = false;
          for (const clipped of clippedRuns) {
            if (!clipped || clipped.length < 2) continue;
            const lineLen = lineLengthM(clipped);
            if (lineLen < MIN_LINE_LENGTH_M) continue;

            depthsSampled.push(waterDepthM); spacingsSampled.push(spacing);
            const lineNum = generated.length + 1;
            generated.push({
              type: "Feature",
              properties: {
                name: `L${String(lineNum).padStart(3, "0")}`, lineIndex: lineNum,
                sectionChainageM: Math.round(chainageM * 10) / 10,
                waterDepthM: Math.round(waterDepthM * 10) / 10,
                swathWidthM: Math.round(swathWidth * 10) / 10,
                spacingM: Math.round(spacing * 10) / 10,
                lengthM: Math.round(lineLen * 10) / 10,
              },
              geometry: { type: "LineString", coordinates: clipped },
            });
            addedAtChainage = true;
          }
          if (!addedAtChainage) { chainageM += Math.max(5, spacing * 0.25); continue; }
          chainageM += spacing;
        }
      } else {
        const azimuth = ((state.azimuth % 360) + 360) % 360;
        const crosstrackBearing = (azimuth + 90) % 360;
        const centroid = turf.centroid(polygon);
        let minCT = Infinity, maxCT = -Infinity;
        turf.coordAll(polygon).forEach((coord) => {
          const proj = projectOntoAxis(turf.point(coord), centroid, crosstrackBearing);
          if (proj < minCT) minCT = proj;
          if (proj > maxCT) maxCT = proj;
        });

        let ctOffset = minCT;
        let prevSpacing = Math.max(MIN_SPACING_M, 2 * minDepth * Math.tan(halfSwathRad) * (1 - overlapFrac));

        while (ctOffset <= maxCT + prevSpacing && generated.length < MAX_LINES && !generateAbort) {
          const lineOrigin = turf.destination(centroid, ctOffset, crosstrackBearing, { units: "meters" });
          const ls = turf.destination(lineOrigin, -longLineLen, azimuth, { units: "meters" });
          const le = turf.destination(lineOrigin, longLineLen, azimuth, { units: "meters" });
          const polyClipped = clipLineToPoly([ls.geometry.coordinates, le.geometry.coordinates], polygon);

          if (!polyClipped) {
            if (ctOffset > maxCT) break;
            ctOffset += Math.max(10, prevSpacing * 0.15); continue;
          }
          const clippedRuns = waterGrid ? clipLineToDepthRuns(polyClipped, minDepth) : [polyClipped];
          let nextSpacing = prevSpacing;
          let addedAtOffset = false;
          for (const clipped of clippedRuns) {
            if (!clipped || clipped.length < 2) continue;
            const lineLen = lineLengthM(clipped);
            if (lineLen < MIN_LINE_LENGTH_M) continue;

            const midCoord = clipped[Math.floor(clipped.length / 2)];
            setStatus(`Line ${generated.length + 1}: querying depth…`);
            const { waterDepthM, isLand } = await sampleEtopo(midCoord[0], midCoord[1], minDepth);
            if (isLand) continue;

            const swathWidth = 2 * waterDepthM * Math.tan(halfSwathRad);
            const spacing = Math.max(MIN_SPACING_M, swathWidth * (1 - overlapFrac));
            nextSpacing = addedAtOffset ? Math.min(nextSpacing, spacing) : spacing;
            depthsSampled.push(waterDepthM); spacingsSampled.push(spacing);

            const lineNum = generated.length + 1;
            generated.push({
              type: "Feature",
              properties: {
                name: `L${String(lineNum).padStart(3, "0")}`, lineIndex: lineNum,
                ctOffsetM: Math.round(ctOffset * 10) / 10,
                waterDepthM: Math.round(waterDepthM * 10) / 10,
                swathWidthM: Math.round(swathWidth * 10) / 10,
                spacingM: Math.round(spacing * 10) / 10,
                lengthM: Math.round(lineLen * 10) / 10,
              },
              geometry: { type: "LineString", coordinates: clipped },
            });
            addedAtOffset = true;
          }
          if (!addedAtOffset) { ctOffset += Math.max(10, prevSpacing * 0.15); continue; }
          prevSpacing = nextSpacing;
          ctOffset += prevSpacing;
        }
      }

      if (generateAbort) { setStatus("Cancelled."); return; }

      lines = generated;
      if (lines.length > 0) {
        const totalKm = lines.reduce((s, f) => s + f.properties.lengthM, 0) / 1000;
        const avgSpacing = spacingsSampled.reduce((a, b) => a + b, 0) / spacingsSampled.length;
        stats = {
          count: lines.length, totalKm: Math.round(totalKm * 100) / 100,
          avgSpacingM: Math.round(avgSpacing),
          minSpacingM: Math.round(Math.min(...spacingsSampled)),
          maxSpacingM: Math.round(Math.max(...spacingsSampled)),
          minDepthM: Math.round(Math.min(...depthsSampled) * 10) / 10,
          maxDepthM: Math.round(Math.max(...depthsSampled) * 10) / 10,
        };
        setStatus(`Generated ${lines.length} line${lines.length === 1 ? "" : "s"}.`);
        renderLinesOnMap();
      } else {
        setStatus("No lines fit within the boundary.");
      }
    } catch (err) {
      setStatus(`Error: ${err?.message || "Unknown error"}`);
      console.error("[MBSwathPlanner] generateLines failed", err);
    } finally {
      generating = false; generateAbort = false; render();
    }
  }

  // ── Water grid (shared by contour drawing and line clipping) ─────────────────

  async function buildWaterGrid() {
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) return;
    const bbox = turf.bbox(turf.polygon([[...state.boundaryCoords, state.boundaryCoords[0]]]));
    const padX = (bbox[2] - bbox[0]) * 0.15, padY = (bbox[3] - bbox[1]) * 0.15;
    const b = [bbox[0] - padX, bbox[1] - padY, bbox[2] + padX, bbox[3] + padY];
    const N = 50;
    const lonStep = (b[2] - b[0]) / (N - 1), latStep = (b[3] - b[1]) / (N - 1);
    const grid = new Array(N * N).fill(null);
    await Promise.allSettled(Array.from({ length: N * N }, (_, idx) => {
      const i = idx % N, j = Math.floor(idx / N);
      return app.services.identify.sampleDepth(b[0] + i * lonStep, b[1] + j * latStep, "etopo")
        .then((r) => { const v = Number(r?.depth_m); if (Number.isFinite(v)) grid[j * N + i] = v; })
        .catch(() => {});
    }));
    waterGrid = grid; waterGridBbox = b; waterGridN = N;
  }

  function sampleGridDepth(lon, lat) {
    if (!waterGrid || !waterGridBbox) return null;
    const [bx0, by0, bx1, by1] = waterGridBbox;
    const N = waterGridN;
    const fx = (lon - bx0) / (bx1 - bx0) * (N - 1);
    const fy = (lat - by0) / (by1 - by0) * (N - 1);
    if (fx < 0 || fx > N - 1 || fy < 0 || fy > N - 1) return null;
    const x0 = Math.floor(fx), y0 = Math.floor(fy);
    const x1 = Math.min(N - 1, x0 + 1), y1 = Math.min(N - 1, y0 + 1);
    const tx = fx - x0, ty = fy - y0;
    const v00 = waterGrid[y0 * N + x0], v10 = waterGrid[y0 * N + x1];
    const v01 = waterGrid[y1 * N + x0], v11 = waterGrid[y1 * N + x1];
    if ([v00, v10, v01, v11].every(Number.isFinite)) {
      const a = v00 * (1 - tx) + v10 * tx;
      const b = v01 * (1 - tx) + v11 * tx;
      return a * (1 - ty) + b * ty;
    }
    const ix = Math.round(fx), iy = Math.round(fy);
    const nearest = waterGrid[iy * N + ix];
    return Number.isFinite(nearest) ? nearest : null;
  }

  function isDepthPoint(lon, lat, minDepth) {
    const v = sampleGridDepth(lon, lat);
    return v === null || v <= -Math.max(0.5, Number(minDepth) || 0.5);
  }

  function contourCrossing(a, b, minDepth) {
    const va = sampleGridDepth(a[0], a[1]);
    const vb = sampleGridDepth(b[0], b[1]);
    if (!Number.isFinite(va) || !Number.isFinite(vb) || Math.abs(vb - va) < 1e-9) return null;
    const level = -Math.max(0.5, Number(minDepth) || 0.5);
    const t = Math.max(0, Math.min(1, (level - va) / (vb - va)));
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  }

  function lineLengthM(coords) {
    let total = 0;
    for (let i = 0; i < coords.length - 1; i++) total += turf.distance(turf.point(coords[i]), turf.point(coords[i + 1]), { units: "meters" });
    return total;
  }

  function clipLineToDepthRuns(coords, minDepth) {
    if (!waterGrid) return [coords];
    const stepDeg = Math.min(waterGridBbox[2] - waterGridBbox[0], waterGridBbox[3] - waterGridBbox[1]) / (waterGridN - 1) * 0.35;
    const dense = [];
    for (let i = 0; i < coords.length - 1; i++) {
      const [x0, y0] = coords[i], [x1, y1] = coords[i + 1];
      const dx = x1 - x0, dy = y1 - y0;
      const steps = Math.max(2, Math.ceil(Math.sqrt(dx * dx + dy * dy) / Math.max(stepDeg, 1e-9)));
      for (let s = 0; s < steps; s++) dense.push([x0 + dx * s / steps, y0 + dy * s / steps]);
    }
    dense.push(coords[coords.length - 1]);

    const ok = dense.map(([lon, lat]) => isDepthPoint(lon, lat, minDepth));
    const runs = [];
    let run = [];
    for (let i = 0; i < dense.length; i++) {
      if (ok[i]) {
        if (!run.length && i > 0 && !ok[i - 1]) {
          const x = contourCrossing(dense[i - 1], dense[i], minDepth);
          if (x) run.push(x);
        }
        run.push(dense[i]);
      } else if (run.length) {
        const x = i > 0 ? contourCrossing(dense[i - 1], dense[i], minDepth) : null;
        if (x) run.push(x);
        if (run.length >= 2) runs.push(run);
        run = [];
      }
    }
    if (run.length >= 2) runs.push(run);
    return runs;
  }

  // ── Minimum-depth contour ─────────────────────────────────────────────────────

  async function draw0mContour() {
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) { app.ui.toast("Draw a boundary first."); return; }
    if (contourDrawn) {
      contourLayer.clearLayers(); contourDrawn = false; render(); return;
    }
    const minDepth = Math.max(0.5, Number(state.minDepth) || 0.5);
    const level = -minDepth;
    setStatus(`Sampling ETOPO for ${minDepth}m contour…`); render();
    try {
      if (!waterGrid) await buildWaterGrid();
      const b = waterGridBbox, N = waterGridN;
      const lonStep = (b[2] - b[0]) / (N - 1), latStep = (b[3] - b[1]) / (N - 1);
      const grid = waterGrid;

      // Marching squares lookup: corners BL/BR/TR/TL, 1=deep enough, 0=too shallow/land
      // bits: 0=BL 1=BR 2=TR 3=TL
      const MS = [
        [],                      // 0
        [["B","L"]],             // 1  BL
        [["B","R"]],             // 2  BR
        [["L","R"]],             // 3  BL+BR
        [["R","T"]],             // 4  TR
        [["B","L"],["R","T"]],   // 5  BL+TR saddle
        [["B","T"]],             // 6  BR+TR
        [["L","T"]],             // 7  BL+BR+TR
        [["L","T"]],             // 8  TL
        [["B","T"]],             // 9  BL+TL
        [["B","R"],["L","T"]],   // 10 BR+TL saddle
        [["R","T"]],             // 11 BL+BR+TL
        [["L","R"]],             // 12 TR+TL
        [["B","R"]],             // 13 BL+TR+TL
        [["B","L"]],             // 14 BR+TR+TL
        [],                      // 15
      ];
      const lerp = (va, vb, a, b2) => {
        const t = Math.abs(vb - va) < 1e-10 ? 0.5 : (level - va) / (vb - va);
        return [a[0] + t * (b2[0] - a[0]), a[1] + t * (b2[1] - a[1])];
      };
      const segments = [];
      for (let j = 0; j < N - 1; j++) {
        for (let i = 0; i < N - 1; i++) {
          const v00 = grid[j * N + i], v10 = grid[j * N + i + 1];
          const v11 = grid[(j+1) * N + i + 1], v01 = grid[(j+1) * N + i];
          if (v00 === null || v10 === null || v11 === null || v01 === null) continue;
          const code = (v00<=level?1:0) | (v10<=level?2:0) | (v11<=level?4:0) | (v01<=level?8:0);
          if (code === 0 || code === 15) continue;
          const pBL = [b[0]+i*lonStep, b[1]+j*latStep], pBR = [b[0]+(i+1)*lonStep, b[1]+j*latStep];
          const pTR = [b[0]+(i+1)*lonStep, b[1]+(j+1)*latStep], pTL = [b[0]+i*lonStep, b[1]+(j+1)*latStep];
          const pts = { B: lerp(v00,v10,pBL,pBR), R: lerp(v10,v11,pBR,pTR), T: lerp(v01,v11,pTL,pTR), L: lerp(v00,v01,pBL,pTL) };
          for (const [a, c] of MS[code]) segments.push([pts[a], pts[c]]);
        }
      }
      contourLayer.clearLayers();
      segments.forEach(([p1, p2]) => L.polyline([[p1[1],p1[0]],[p2[1],p2[0]]], { color:"#4ade80", weight:1.5, opacity:0.9, interactive:false }).addTo(contourLayer));
      contourDrawn = segments.length > 0;
      setStatus(contourDrawn ? `${minDepth}m contour: ${segments.length} segments.` : `No ${minDepth}m contour crossings found in boundary area.`);
      render();
    } catch (err) {
      setStatus("Contour error."); console.error("[MBSwathPlanner] draw0mContour failed", err);
    }
  }

  // ── Exports ───────────────────────────────────────────────────────────────────

  function dl(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 300);
  }

  function exportGeoJson() {
    if (!lines.length) return;
    dl(new Blob([JSON.stringify({
      type: "FeatureCollection", features: lines,
      properties: { mode: state.mode, azimuth: state.azimuth, swathAngle: state.swathAngle, overlap: state.overlap, generated: new Date().toISOString() },
    }, null, 2)], { type: "application/geo+json" }), `mbswath_lines_${Date.now()}.geojson`);
  }

  function exportHypack() {
    if (!lines.length) return;
    const out = [`LNS ${lines.length}`];
    lines.forEach((f) => {
      const coords = f.geometry.coordinates;
      out.push(`LIN ${coords.length}`);
      coords.forEach(([lon, lat]) => out.push(`PTS ${lon.toFixed(8)} ${lat.toFixed(8)}`));
      out.push(`LNN ${f.properties.name}`); out.push("EOL");
    });
    dl(new Blob([`${out.join("\n")}\n`], { type: "text/plain" }), `mbswath_lines_${Date.now()}.lnw`);
  }

  async function exportKmz() {
    if (!lines.length) return;
    const JSZip = (await import("jszip")).default;
    const placemarks = lines.map((f) => {
      const p = f.properties;
      const coords = f.geometry.coordinates.map(([lon, lat]) => `${lon},${lat},0`).join(" ");
      return `  <Placemark><name>${esc(p.name)}</name>
    <description>Depth: ${p.waterDepthM}m | Swath: ${p.swathWidthM}m | Spacing: ${p.spacingM}m | Length: ${p.lengthM}m</description>
    <Style><LineStyle><color>ffeed322</color><width>2</width></LineStyle></Style>
    <LineString><tessellate>1</tessellate><coordinates>${coords}</coordinates></LineString>
  </Placemark>`;
    }).join("\n");
    const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2"><Document>
  <name>MBSWATH Survey Lines</name>
  <description>Mode: ${state.mode} · Swath ${state.swathAngle}° · Overlap ${state.overlap}%</description>
${placemarks}
</Document></kml>`;
    const zip = new JSZip();
    zip.file("survey_lines.kml", kml);
    dl(await zip.generateAsync({ type: "blob" }), `mbswath_lines_${Date.now()}.kmz`);
  }

  // ── UI ────────────────────────────────────────────────────────────────────────

  function render() {
    if (!containerEl) return;
    const hasBoundary = !!(state.boundaryCoords && state.boundaryCoords.length >= 3);
    const hasSpine = !!(state.spineCoords && state.spineCoords.length >= 2);
    const hasSection = !!(state.sectionCoords && state.sectionCoords.length >= 2);
    const hasLines = lines.length > 0;
    const calc = previewCalc(state.swathAngle, state.minDepth, state.overlap);
    const s = stats;
    const isSpine = state.mode === "spine";
    const isSection = state.mode === "section";
    const canGenerate = hasBoundary && (!isSpine || hasSpine) && (!isSection || hasSection);

    containerEl.innerHTML = `
<div style="padding:10px 8px;display:flex;flex-direction:column;gap:10px;">

  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:6px;">Survey Boundary</div>
    <div class="row" style="gap:6px;flex-wrap:wrap;">
      <button id="mbslp-draw" class="btn${drawingBoundary ? " primary" : ""}" style="font-size:11px;">
        ${drawingBoundary ? "⬛ Finish / Esc" : "✏ Draw Boundary"}
      </button>
      <label class="btn" style="font-size:11px;cursor:pointer;margin:0;" title="Load KMZ, KML, or GeoJSON boundary">
        📂 Import
        <input type="file" id="mbslp-import-file" accept=".kmz,.kml,.geojson,.json" style="display:none;" />
      </label>
      ${hasBoundary && !drawingBoundary ? `<button id="mbslp-clear-bnd" class="btn" style="font-size:11px;color:#f87171;border-color:#f8717144;">✕ Clear</button>` : ""}
    </div>
    <div class="small" style="margin-top:5px;color:${drawingBoundary ? "#f59e0b" : hasBoundary ? "#22c55e" : "var(--muted)"};">
      ${drawingBoundary
        ? `Click to add vertices <span id="mbslp-draw-count" style="font-weight:600;">${drawVertices.length ? drawVertices.length + " pts" : ""}</span>`
        : hasBoundary ? `✓ ${state.boundaryCoords.length} vertices` : "No boundary drawn."}
    </div>
    <div style="margin-top:6px;padding-top:6px;border-top:1px solid var(--border);">
      <button id="mbslp-contour" class="btn${contourDrawn ? " primary" : ""}" style="font-size:11px;" ${!hasBoundary ? "disabled" : ""}>
        ${contourDrawn ? `✕ Clear ${state.minDepth}m Contour` : `〰 ${state.minDepth}m Contour`}
      </button>
    </div>
  </div>

  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:6px;">Line Mode</div>
    <div class="row" style="gap:0;border:1px solid var(--border);border-radius:5px;overflow:hidden;">
      <button id="mbslp-mode-azimuth" class="btn" style="flex:1;border-radius:0;border:none;font-size:11px;${!isSpine && !isSection ? "background:var(--accent-bg,#1e3a5f);color:#38bdf8;" : ""}">Azimuth</button>
      <button id="mbslp-mode-spine" class="btn" style="flex:1;border-radius:0;border:none;border-left:1px solid var(--border);font-size:11px;${isSpine ? "background:var(--accent-bg,#1e3a5f);color:#a78bfa;" : ""}">Spine</button>
      <button id="mbslp-mode-section" class="btn" style="flex:1;border-radius:0;border:none;border-left:1px solid var(--border);font-size:11px;${isSection ? "background:var(--accent-bg,#1e3a5f);color:#fb7185;" : ""}">Section</button>
    </div>
  </div>

  ${!isSpine && !isSection ? `
  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:8px;">Parameters</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 12px;">
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Line Azimuth${drawingAzimuth ? ` <span id="mbslp-az-live" style="color:#38bdf8;font-weight:600;font-size:10px;">click pt 1…</span>` : ""}
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-azimuth" type="number" min="0" max="359" step="1" value="${state.azimuth}" style="width:52px;" />
          <span class="small" style="color:var(--muted);">°</span>
          <span class="small" style="color:#38bdf8;font-size:10px;font-weight:600;">${azimuthLabel(state.azimuth)}</span>
          <button id="mbslp-draw-az" class="btn${drawingAzimuth ? " primary" : ""}" style="font-size:10px;padding:1px 5px;line-height:1.4;" title="Draw azimuth on map">↗</button>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Full Swath Angle
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-swath" type="number" min="1" max="179" step="1" value="${state.swathAngle}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">°</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Overlap
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-overlap" type="number" min="0" max="95" step="1" value="${state.overlap}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">%</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;" title="Minimum depth for swath calc; land positions skipped">
        Min Depth Floor
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-mindepth" type="number" min="0.5" max="500" step="0.5" value="${state.minDepth}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">m</span>
        </div>
      </label>
    </div>
    <div style="margin-top:7px;padding-top:6px;border-top:1px solid var(--border);display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
      <div class="small" style="flex:1;color:var(--muted);font-size:10px;line-height:1.5;">
        At ${state.minDepth}m → swath <strong style="color:#e2e8f0;">${calc.swath}m</strong> → spacing <strong style="color:#e2e8f0;">${calc.spacing}m</strong>
        &nbsp;·&nbsp; land skipped &nbsp;·&nbsp; lines at ${state.azimuth}° (${azimuthLabel(state.azimuth)})
      </div>
      <button id="mbslp-auto-orient" class="btn" style="font-size:10px;padding:2px 7px;white-space:nowrap;" title="Sample ETOPO inside boundary and set azimuth along-isobath">🧭 Auto</button>
    </div>
  </div>
  ` : isSection ? `
  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:5px;">Section Line</div>
    <div class="small" style="color:var(--muted);font-size:10px;margin-bottom:7px;line-height:1.5;">Draw the upslope/downslope section. ETOPO is sampled along this line, then survey lines are generated perpendicular to it at depth-based intervals.</div>
    <div class="row" style="gap:6px;flex-wrap:wrap;">
      <button id="mbslp-draw-section" class="btn${drawingSection ? " primary" : ""}" style="font-size:11px;">
        ${drawingSection ? "⬛ Finish / Esc" : "✏ Draw Section"}
      </button>
      ${hasSection && !drawingSection ? `<button id="mbslp-clear-section" class="btn" style="font-size:11px;color:#f87171;border-color:#f8717144;">✕ Clear Section</button>` : ""}
    </div>
    <div class="small" style="margin-top:5px;color:${drawingSection ? "#fb7185" : hasSection ? "#22c55e" : "var(--muted)"};">
      ${drawingSection
        ? `Click to add points <span id="mbslp-section-count" style="font-weight:600;">${sectionVertices.length ? sectionVertices.length + " pts" : ""}</span>`
        : hasSection ? `✓ ${state.sectionCoords.length} vertices` : "No section drawn."}
    </div>
    <div style="margin-top:8px;padding-top:6px;border-top:1px solid var(--border);display:grid;grid-template-columns:1fr 1fr;gap:8px 12px;">
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Sample Interval
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-section-sample" type="number" min="1" max="5000" step="1" value="${state.sectionSampleSpacing}" style="width:64px;" />
          <span class="small" style="color:var(--muted);">m</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Full Swath Angle
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-swath" type="number" min="1" max="179" step="1" value="${state.swathAngle}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">°</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Overlap
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-overlap" type="number" min="0" max="95" step="1" value="${state.overlap}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">%</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Min Depth Floor
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-mindepth" type="number" min="0.5" max="500" step="0.5" value="${state.minDepth}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">m</span>
        </div>
      </label>
    </div>
    <div class="small" style="margin-top:6px;color:var(--muted);font-size:10px;">
      At ${state.minDepth}m → swath <strong style="color:#e2e8f0;">${calc.swath}m</strong> → spacing <strong style="color:#e2e8f0;">${calc.spacing}m</strong> &nbsp;·&nbsp; lines perpendicular to section
    </div>
  </div>
  ` : `
  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:5px;">Spine Line</div>
    <div class="small" style="color:var(--muted);font-size:10px;margin-bottom:7px;line-height:1.5;">Draw a curved reference through the survey area. Lines generate perpendicular to the spine's local direction at each step, following its curvature.</div>
    <div class="row" style="gap:6px;flex-wrap:wrap;">
      <button id="mbslp-draw-spine" class="btn${drawingSpine ? " primary" : ""}" style="font-size:11px;">
        ${drawingSpine ? "⬛ Finish / Esc" : "✏ Draw Spine"}
      </button>
      ${hasSpine && !drawingSpine ? `<button id="mbslp-clear-spine" class="btn" style="font-size:11px;color:#f87171;border-color:#f8717144;">✕ Clear Spine</button>` : ""}
    </div>
    <div class="small" style="margin-top:5px;color:${drawingSpine ? "#a78bfa" : hasSpine ? "#22c55e" : "var(--muted)"};">
      ${drawingSpine
        ? `Click to add points <span id="mbslp-spine-count" style="font-weight:600;">${spineVertices.length ? spineVertices.length + " pts" : ""}</span>`
        : hasSpine ? `✓ ${state.spineCoords.length} vertices` : "No spine drawn."}
    </div>
    <div style="margin-top:8px;padding-top:6px;border-top:1px solid var(--border);display:grid;grid-template-columns:1fr 1fr;gap:8px 12px;">
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Full Swath Angle
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-swath" type="number" min="1" max="179" step="1" value="${state.swathAngle}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">°</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Overlap
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-overlap" type="number" min="0" max="95" step="1" value="${state.overlap}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">%</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Min Depth Floor
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-mindepth" type="number" min="0.5" max="500" step="0.5" value="${state.minDepth}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">m</span>
        </div>
      </label>
    </div>
    <div class="small" style="margin-top:6px;color:var(--muted);font-size:10px;">
      At ${state.minDepth}m → swath <strong style="color:#e2e8f0;">${calc.swath}m</strong> → spacing <strong style="color:#e2e8f0;">${calc.spacing}m</strong> &nbsp;·&nbsp; land skipped
    </div>
  </div>
  `}

  <button id="mbslp-generate" class="btn primary"
    ${!canGenerate || (generating && !generateAbort) ? "disabled" : ""}
    style="font-size:12px;padding:6px 10px;">
    ${generating ? "⏹ Cancel" : "⚡ Generate Lines"}
  </button>

  <div id="mbslp-status" class="small" style="min-height:14px;color:var(--muted);">${esc(status)}</div>

  ${hasLines && s ? `
  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:7px;">Results</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:3px 14px;font-size:11px;line-height:1.6;">
      <div><span style="color:var(--muted);">Lines</span> <strong>${s.count}</strong></div>
      <div><span style="color:var(--muted);">Total length</span> <strong>${s.totalKm} km</strong></div>
      <div><span style="color:var(--muted);">Avg spacing</span> <strong>${s.avgSpacingM} m</strong></div>
      <div><span style="color:var(--muted);">Min/Max spacing</span> <strong>${s.minSpacingM}–${s.maxSpacingM} m</strong></div>
      <div><span style="color:var(--muted);">Shallowest</span> <strong>${s.minDepthM} m</strong></div>
      <div><span style="color:var(--muted);">Deepest</span> <strong>${s.maxDepthM} m</strong></div>
    </div>
    <div class="row" style="gap:6px;margin-top:8px;flex-wrap:wrap;">
      <button id="mbslp-ex-geojson" class="btn" style="font-size:11px;">GeoJSON</button>
      <button id="mbslp-ex-hypack" class="btn" style="font-size:11px;">HYPACK</button>
      <button id="mbslp-ex-kmz" class="btn" style="font-size:11px;">KMZ</button>
      <button id="mbslp-clear-lines" class="btn" style="font-size:11px;color:#f87171;border-color:#f8717144;">✕ Clear</button>
    </div>
  </div>
  ` : ""}

  ${buildStatHtml()}

</div>`;

    bindControls();
  }

  function bindControls() {
    if (!containerEl) return;

    containerEl.querySelector("#mbslp-draw")?.addEventListener("click", () => {
      if (drawingBoundary) finishDraw(); else startDrawBoundary();
    });
    containerEl.querySelector("#mbslp-import-file")?.addEventListener("change", (e) => {
      const file = e.target?.files?.[0];
      if (file) importBoundaryFile(file);
      e.target.value = "";
    });
    containerEl.querySelector("#mbslp-clear-bnd")?.addEventListener("click", () => {
      state.boundaryCoords = null; lines = []; stats = null; status = "";
      boundaryLayer.clearLayers(); linesLayer.clearLayers();
      contourLayer.clearLayers(); contourDrawn = false;
      waterGrid = null;
      persist(); render();
    });
    containerEl.querySelector("#mbslp-contour")?.addEventListener("click", () => draw0mContour());

    containerEl.querySelector("#mbslp-mode-azimuth")?.addEventListener("click", () => {
      if (state.mode === "azimuth") return;
      if (drawingSpine) stopSpineDraw("cancelled");
      if (drawingSection) stopSectionDraw("cancelled");
      state.mode = "azimuth"; lines = []; stats = null; status = "";
      linesLayer.clearLayers(); persist(); render();
    });
    containerEl.querySelector("#mbslp-mode-spine")?.addEventListener("click", () => {
      if (state.mode === "spine") return;
      if (drawingAzimuth) stopDrawAzimuth("cancelled");
      if (drawingSection) stopSectionDraw("cancelled");
      state.mode = "spine"; lines = []; stats = null; status = "";
      linesLayer.clearLayers(); persist(); render();
    });
    containerEl.querySelector("#mbslp-mode-section")?.addEventListener("click", () => {
      if (state.mode === "section") return;
      if (drawingAzimuth) stopDrawAzimuth("cancelled");
      if (drawingSpine) stopSpineDraw("cancelled");
      state.mode = "section"; lines = []; stats = null; status = "";
      linesLayer.clearLayers(); persist(); render();
    });

    containerEl.querySelector("#mbslp-draw-az")?.addEventListener("click", () => {
      if (drawingAzimuth) stopDrawAzimuth("cancelled"); else startDrawAzimuth();
    });
    containerEl.querySelector("#mbslp-auto-orient")?.addEventListener("click", () => autoOrientFromBathymetry());

    containerEl.querySelector("#mbslp-draw-spine")?.addEventListener("click", () => {
      if (drawingSpine) finishSpineDraw(); else startDrawSpine();
    });
    containerEl.querySelector("#mbslp-clear-spine")?.addEventListener("click", () => {
      state.spineCoords = null; lines = []; stats = null; status = "";
      spineLayer.clearLayers(); linesLayer.clearLayers(); persist(); render();
    });

    containerEl.querySelector("#mbslp-draw-section")?.addEventListener("click", () => {
      if (drawingSection) finishSectionDraw(); else startDrawSection();
    });
    containerEl.querySelector("#mbslp-clear-section")?.addEventListener("click", () => {
      state.sectionCoords = null; lines = []; stats = null; status = "";
      sectionLayer.clearLayers(); linesLayer.clearLayers(); persist(); render();
    });

    containerEl.querySelector("#mbslp-generate")?.addEventListener("click", () => {
      if (generating) generateAbort = true; else generateLines();
    });
    containerEl.querySelector("#mbslp-ex-geojson")?.addEventListener("click", exportGeoJson);
    containerEl.querySelector("#mbslp-ex-hypack")?.addEventListener("click", exportHypack);
    containerEl.querySelector("#mbslp-ex-kmz")?.addEventListener("click", () => exportKmz().catch(console.error));
    containerEl.querySelector("#mbslp-clear-lines")?.addEventListener("click", () => {
      lines = []; stats = null; status = ""; linesLayer.clearLayers(); render();
    });

    const numInput = (id, setter) => {
      const el = containerEl.querySelector(id);
      if (!el) return;
      el.addEventListener("change", () => { setter(Number(el.value)); persist(); render(); });
    };
    numInput("#mbslp-azimuth", (v) => { state.azimuth = Math.max(0, Math.min(359, v || 0)); });
    numInput("#mbslp-swath", (v) => { state.swathAngle = Math.max(1, Math.min(179, v || 120)); });
    numInput("#mbslp-overlap", (v) => { state.overlap = Math.max(0, Math.min(95, v || 10)); });
    numInput("#mbslp-mindepth", (v) => {
      state.minDepth = Math.max(0.5, v || 5);
      contourLayer.clearLayers(); contourDrawn = false; waterGrid = null;
    });
    numInput("#mbslp-section-sample", (v) => { state.sectionSampleSpacing = Math.max(1, Math.min(5000, v || 25)); });

    // ── Statistical LNM Estimator bindings ────────────────────────────────
    containerEl.querySelector("#mbslp-stat-draw")?.addEventListener("click", () => {
      if (statDrawing) cancelStatDraw(); else startStatDraw();
    });
    containerEl.querySelector("#mbslp-stat-clear-all")?.addEventListener("click", clearAllStatAOIs);

    // File import
    const statFileEl = containerEl.querySelector("#mbslp-stat-file");
    if (statFileEl) statFileEl.addEventListener("change", async () => {
      for (const file of Array.from(statFileEl.files)) await importStatFile(file);
      statFileEl.value = "";
    });

    // DAM picker
    containerEl.querySelector("#mbslp-stat-dam")?.addEventListener("click", () => {
      if (statDamOpen) { statDamOpen = false; render(); } else loadStatDamFiles();
    });
    containerEl.querySelectorAll("[data-stat-dam-key]").forEach(el => {
      el.addEventListener("click", () => openStatDamFile(el.dataset.statDamKey, el.dataset.statDamName));
    });

    // Per-AOI controls (delegated via data attributes, re-wired each render)
    containerEl.querySelectorAll("[data-stat-toggle]").forEach(el => {
      el.addEventListener("click", (ev) => {
        if (ev.target.closest("button")) return;
        const aoi = statAOIs.find(a => a.id === el.dataset.statToggle);
        if (aoi) { aoi.collapsed = !aoi.collapsed; render(); }
      });
    });
    containerEl.querySelectorAll("[data-stat-recompute]").forEach(el => {
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        const aoi = statAOIs.find(a => a.id === el.dataset.statRecompute);
        if (aoi && !aoi.computing) computeStatForAOI(aoi);
      });
    });
    containerEl.querySelectorAll("[data-stat-remove]").forEach(el => {
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        removeStatAOI(el.dataset.statRemove);
      });
    });

    const statNum = (id, setter) => {
      const el = containerEl.querySelector(id);
      if (!el) return;
      el.addEventListener("change", () => { setter(Number(el.value)); });
    };
    statNum("#mbslp-stat-cov",   (v) => { statCovXwd = v || 4; recomputeAllStat(); });
    statNum("#mbslp-stat-xl",    (v) => { statCrosslinePct = v || 0; recomputeAllStat(); });
    statNum("#mbslp-stat-shoal", (v) => { statShoalPct = v || 0; recomputeAllStat(); });
    statNum("#mbslp-stat-runin", (v) => { statRunInM = v || 0; recomputeAllStat(); });

    const srcEl = containerEl.querySelector("#mbslp-stat-src");
    if (srcEl) srcEl.addEventListener("change", () => { statDepthSrc = srcEl.value; });

    const encCbEl = containerEl.querySelector("#mbslp-stat-enc");
    if (encCbEl) encCbEl.addEventListener("change", () => { statUseEnc = encCbEl.checked; });

    const contourCbEl = containerEl.querySelector("#mbslp-stat-contours");
    if (contourCbEl) contourCbEl.addEventListener("change", () => {
      statShowContours = contourCbEl.checked;
      if (statShowContours && statAOIs.some(a => a.bins.some(b => b.depthM != null))) generateStatContours().catch(console.warn);
      else statContourLayer.clearLayers();
    });

    const labelCbEl = containerEl.querySelector("#mbslp-stat-labels");
    if (labelCbEl) labelCbEl.addEventListener("change", () => {
      statShowLabels = labelCbEl.checked;
      updateStatLabels();
    });

    containerEl.querySelectorAll("[data-cost-range]").forEach(el => {
      el.addEventListener("change", () => {
        statCostRates[el.dataset.costRange] = Number(el.value) || 0;
        saveCostRates();
        render();
      });
    });

    containerEl.querySelector("#mbslp-stat-print")?.addEventListener("click", () => {
      printReport().catch(e => app.ui.toast("Report failed: " + (e?.message || String(e))));
    });
  }

  function mount(el) {
    containerEl = el;
    renderBoundaryOnMap(); renderSpineOnMap(); renderSectionOnMap(); renderLinesOnMap(); render();
  }

  function destroy() {
    stopDraw("destroy"); stopDrawAzimuth("destroy"); stopSpineDraw("destroy"); stopSectionDraw("destroy");
    try { boundaryLayer.clearLayers(); map.removeLayer(boundaryLayer); } catch {}
    try { spineLayer.clearLayers(); map.removeLayer(spineLayer); } catch {}
    try { sectionLayer.clearLayers(); map.removeLayer(sectionLayer); } catch {}
    try { linesLayer.clearLayers(); map.removeLayer(linesLayer); } catch {}
    try { drawLayer.clearLayers(); map.removeLayer(drawLayer); } catch {}
    try { contourLayer.clearLayers(); map.removeLayer(contourLayer); } catch {}
    cancelStatDraw();
    statAOIs.forEach(aoi => { aoi.bins.forEach(b => b.layer?.remove()); aoi.outlineLayer?.remove(); });
    statAOIs = [];
    try { statLayer.clearLayers(); map.removeLayer(statLayer); } catch {}
    try { statContourLayer.clearLayers(); map.removeLayer(statContourLayer); } catch {}
    try { statLabelLayer.clearLayers(); map.removeLayer(statLabelLayer); } catch {}
    containerEl = null;
  }

  return { mount, destroy };
}

export const mbswathPlannerTool = {
  id: "mbswath-planner",
  name: "MBSWATH Planner",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};
